// Scattering LUTs for one body's atmosphere.
//
// Hillaire 2020 ("A Scalable and Production Ready Sky and Atmosphere Rendering
// Technique") rather than Bruneton 2008: WebGL2 cannot render into a 3D texture
// without attaching one layer at a time, so Bruneton's two 3D scattering tables
// would be 32 to 128 extra passes at precompute and a 3D sampler in every
// consumer. Hillaire's set is all 2D - transmittance, a multiple-scattering
// factor, and a sky-view table - and aerial perspective is a short analytic
// march per fragment against the first two instead of a froxel volume.
//
// The transmittance table's (r, mu) parameterization is Bruneton's, because it
// is the one that makes T(a->b) = T(a->top) / T(b->top) exact, which is what
// the aerial march in `ATMO_SAMPLE_GLSL` needs.
//
// `src/atlas/atmoSky.ts` samples the sky-view table for the sky at every band
// and for the ORBIT limb; WP4b injects the aerial term into the tile and plot
// shaders. Beside the tables and that sampling chunk there is a readback the
// instrument checks against a CPU reference (`tools/preview/atmo-check.mjs
// --gpu`).

import * as THREE from 'three';
import { devFlag, urlFlag, TEST_SWITCHES } from '../urlFlags.ts';
import { columnTau550, deckAlbedo, groundAlbedoRgb } from '../world/bodies.ts';
import type { Atmosphere, BodyRow, MieLobes } from '../world/types';
import { createDustTables, DUST_GRID, DUST_LOOK_GLSL, dustGlslDefines, type DustGrid } from './dustTables.ts';

/** `?oldair=1`: the air as it was before overcast skies - conservative grey
 *  haze clipped per channel, which draws Titan's face grey, the full direct
 *  beam on every ground and the camera metered off a deck's
 *  cloud top under it. `descent-dojo --overcast --inject old-air` reaches it. */
export const oldAir = devFlag(() => TEST_SWITCHES && urlFlag('oldair'));

// ── sizes and thresholds ────────────────────────────────────────────────────

const TRANSMITTANCE_W = 256;
const TRANSMITTANCE_H = 64;
const MULTI_SCATTER_W = 32;
const MULTI_SCATTER_H = 32;
/** The ground's sky irradiance against its sun cosine (a dust row's MS build), and its cosine rows; azimuths are twice. */
const GROUND_DIFFUSE_W = 32;
const GROUND_DIFFUSE_ROWS = 12;
/**
 * Terms the multiple-scattering table holds side by side in one texture.
 *
 * Hillaire's Psi_ms is exactly LINEAR in the ground albedo - `f_ms` carries no
 * ground term at all and `L_2nd`'s only one is a Lambertian bounce - so the
 * table is split into Psi_atm (albedo 0) and Psi_gnd (the albedo-1 bounce), and
 * a terrain fragment evaluates `Psi_atm + A_local * Psi_gnd` with its OWN
 * albedo instead of taking the whole planet's mean as a flat wash.
 *
 * Two blocks of `MULTI_SCATTER_W` columns in ONE 64x32 target rather than two
 * 32x32 targets: both terms come out of the same 36-direction march, so two
 * targets would mean two passes each re-running that march for its own half -
 * identical GPU work, one more texture, one more pass, one more thing to
 * dispose. `uAtmoMultiScatterSize` stays the LOGICAL 32x32, and each block's
 * unit coordinate maps onto its own texel centres so bilinear never crosses
 * the seam at column 31.5 and mixes muSun +1 with muSun -1.
 */
const MULTI_SCATTER_TERMS = 2;
const SKY_VIEW_W = 200;
const SKY_VIEW_H = 100;

/**
 * Where the shell's top sits, in scale heights above the ground.
 *
 * Eight, so the column the tables integrate is within 0.04 percent of the
 * unbounded `sum(beta * H)` the parameters imply - which is the number
 * `atmo-check --gpu` gates the zenith optical depth against.
 */
const ATMOSPHERE_TOP_SCALE_HEIGHTS = 8;

/** Change in the sun's height, in degrees, that invalidates a sky-view table drawn alone (`skySteps`). */
const SKY_VIEW_SUN_MOVE_DEG = 0.5;
/** The same with no earlier table to draw a line through, as after a scrub. */
const SKY_VIEW_FIRST_MOVE_DEG = 0.05;
/**
 * The most it grows to between paired tables. The line misses the bent twilight
 * sky by about the reach times the spacing, and all of it lands on the next table's frame.
 */
const SKY_VIEW_PAIR_MOVE_DEG = 0.25;
/** Camera altitude ratio that invalidates the sky-view table. */
const SKY_VIEW_ALTITUDE_RATIO = 1.3;
/**
 * Between rebuilds the sky is drawn linear in the sun's height through the last
 * two tables, so it follows the sun every frame instead of holding still for
 * `SKY_VIEW_SUN_MOVE_DEG` and then jumping: near the horizon half a degree is
 * several grey levels of sky. Two tables further apart than this (sine of the
 * sun's height) are no line, and the newest is drawn alone.
 */
const SKY_VIEW_PAIR_MAX_MU = Math.sin((2 * Math.PI) / 180);
/** Furthest the line is followed past the newest table, in tables' spacings. */
const SKY_VIEW_EXTRAP_MAX = 3;
const sunHeightDeg = (mu: number): number => Math.asin(THREE.MathUtils.clamp(mu, -1, 1)) * 180 / Math.PI;
/** Two tables built further apart in altitude than this ratio are not paired. */
const SKY_VIEW_PAIR_ALTITUDE_RATIO = 1.05;
/**
 * How far past the newest table a sky-view pass may still be yielded to the
 * tile mounter, in multiples of the move that owed it: the line's error grows
 * with the square of the reach, and past this it is the larger cost than one
 * pass on a frame that built a tile.
 */
const SKY_VIEW_YIELD_MAX_EXTRAP = 1.5;
/**
 * Altitudes under this are one altitude as far as the sky-view table is
 * concerned: a 1.3x ratio on a metre of camera ease would rebuild it every
 * frame while the camera settles on the ground.
 */
const SKY_VIEW_ALTITUDE_FLOOR_M = 10;
/**
 * Relative change in the aerosol column optical depth that invalidates every
 * table.
 *
 * Mars's `tau` is a function of Ls, so a year sweep moves it a little every
 * frame; without a threshold the transmittance table would be rebuilt on every
 * one of them.
 */
const TAU_RECOMPUTE_FRACTION = 0.02;

/**
 * Frames a pass may be yielded to the tile mounter before it runs regardless.
 *
 * The yield exists so a table is never built on a frame that also builds a tile
 * mesh, which is where the legs Phase 2 cleared spend their budget. What it did
 * NOT allow for is that the mounter runs on every frame while the pyramid
 * streams: at CLAIM the sky-view pass was yielded 36 frames running and five of
 * `descent-dojo --sky`'s eight samples were drawn against a table built for a
 * sun the camera had left. So the yield is a preference with a deadline rather
 * than a veto - eight frames is 133 ms at cadence, and one pass in nine is the
 * worst it can add to a streaming frame.
 */
const MAX_YIELDED_FRAMES = 8;

/** Raymarch steps per pass. Each is one named constant so tuning is one line. */
const TRANSMITTANCE_STEPS = 64;
const MULTI_SCATTER_STEPS = 16;
/** Square root of the direction count the multiple-scattering pass integrates over. */
const MULTI_SCATTER_SQRT_DIRS = 6;
const SKY_VIEW_STEPS = 30;
/** Steps in the per-fragment aerial-perspective march consumers run. */
const AERIAL_STEPS = 8;

export type AtmosphereLutName = 'transmittance' | 'multipleScattering' | 'skyView';

/** How many passes `stats().recentPasses` keeps. A precompute window is
 *  three, and every arm that reads the log takes a baseline first. */
const PASS_LOG = 64;

const LUT_NAMES: readonly AtmosphereLutName[] = ['transmittance', 'multipleScattering', 'skyView'];

export interface AtmosphereParams {
  readonly bodyId: string;
  readonly rGroundM: number;
  readonly rTopM: number;
  readonly hRayleighM: number;
  readonly hAerosolM: number;
  readonly betaR: readonly [number, number, number];
  readonly betaM: number;
  /** Share of the aerosol's extinction that scatters, per channel. */
  readonly mieSsa: readonly [number, number, number];
  /** The aerosol's extinction per channel over `betaM`. */
  readonly mieExt: readonly [number, number, number];
  readonly mieG: number;
  /** The row's two Henyey-Greenstein lobes, which replace `mieG`'s one; null where it carries none. */
  readonly mieLobes: MieLobes | null;
  /** The mean of `groundAlbedoRgb`, which is the row's own broadband number. */
  readonly groundAlbedo: number;
  /** What the ground bounces back into the air, per channel. */
  readonly groundAlbedoRgb: readonly [number, number, number];
  /** The aerosol column optical depth the Mie coefficient was scaled to, or
   *  null where the registry's own `beta_m_550_per_m` was used unscaled. */
  readonly tau: number | null;
  readonly viewHeightM: number;
  /** The eye's TRUE radius, unclamped: what the aerial march starts from. */
  readonly eyeHeightM: number;
  /** The body's own datum radius. Equal to `rGroundM` on every body but one
   *  whose LUT has terminated at a cloud deck kilometres above it. */
  readonly bodyRadiusM: number;
  /** (base, top) altitudes of the opaque deck, or null on a body with none. */
  readonly deckM: readonly [number, number] | null;
  /** Whether the LUT is the sub-cloud shell rather than the air over the deck. */
  readonly underDeck: boolean;
  /** Whether the deck's face is being read with red and blue swapped, which is
   *  `descent-dojo --sky --inject deckflip`'s control and never the game. */
  readonly deckFlip: boolean;
  readonly sunMu: number;
  readonly scaleHeightScale: number;
  readonly betaMScale: number;
  /** Whether the aerial march is running the binary per-sample sun test.
   *  `descent-dojo --veil --inject veil`'s control and never the game. */
  readonly hardSunShadow: boolean;
}

/** One scattering pass, as `descent-dojo --sky` reads it back. */
export interface AtmospherePass {
  /** `passesRun` after this pass, so a window can be sliced by a baseline. */
  readonly seq: number;
  readonly name: AtmosphereLutName;
  /** The pass's own cost. GPU-inclusive where `gpuSynced`, else submit only. */
  readonly ms: number;
  readonly gpuSynced: boolean;
  /** The first run of this table, which is where three.js links its program. */
  readonly first: boolean;
  /** What `injectPassMs` added to `ms`, so an injected run is attributable. */
  readonly injectedMs: number;
}

export interface AtmosphereStats {
  readonly passesRun: number;
  /** Cost of the worst pass, milliseconds - GPU-inclusive only while
   *  `gpuSynced`, since a submit returns before the GPU has drawn anything. */
  readonly worstPassMs: number;
  readonly worstPass: AtmosphereLutName | null;
  readonly lastPassMs: number;
  readonly byPass: Readonly<Record<AtmosphereLutName, number>>;
  /** Worst CPU submit per pass, so one slow table is attributable to itself. */
  readonly msByPass: Readonly<Record<AtmosphereLutName, number>>;
  /** Passes still owed. Zero means every table is current. */
  readonly pending: number;
  readonly ready: boolean;
  /**
   * CPU time spent submitting the dust table's build draws, never GPU time: gl.finish does not wait on the GPU under
   * Chrome's ANGLE, so a GPU reading needs a readback (gl.readPixels) after the draw.
   */
  readonly dustSubmitMs: number;
  /** Dust table builds landed since the mount. */
  readonly dustBuilds: number;
  /** Frames a pass was due on and the tile mounter had the budget. */
  readonly yieldedToTiles: number;
  /** Consecutive frames the pass owed now has been yielded for. */
  readonly yieldedInARow: number;
  /** The last `PASS_LOG` passes, oldest first. */
  readonly recentPasses: readonly AtmospherePass[];
  /** Whether `ms` includes the GPU's own time - see `setDebug`. */
  readonly gpuSynced: boolean;
  /** The busy-wait `setDebug` is adding to every pass, milliseconds. */
  readonly injectPassMs: number;
  /** Whether `setDebug` has put the canvas-pixel viewport back in every pass. */
  readonly canvasViewport: boolean;
  /** Frames `update` refused for a non-finite sun or altitude, keeping the last good ones. */
  readonly nonFiniteRefused: number;
  /** How far past the newest sky-view table's sun height the sky is drawn, in tables' spacings. */
  readonly skyViewExtrap: number;
  readonly skySteps: boolean;
  readonly tilesEveryFrame: boolean;
}

export interface AtmosphereLutRead {
  readonly ok: true;
  readonly name: AtmosphereLutName;
  readonly width: number;
  readonly height: number;
  readonly channels: 4;
  /** RGBA, row 0 first, and row 0 is the table's v = 0 edge. */
  readonly data: number[];
}

export interface AtmosphereHandle {
  /**
   * @param sunDir unit vector toward the sun in the LOCAL HORIZON frame: +y is
   *   the local zenith at the camera's ground point.
   * @param cameraAltitudeM metres above the ground. Clamped into the shell for
   *   the tables, which are only defined inside it, and kept whole for the
   *   aerial march, which needs the eye's true radius to clip its own path.
   * @param tau aerosol column optical depth, or null for the registry value.
   * @param mayRunPass false on a frame the tile mounter already built on, so a
   *   LUT pass never shares a frame with a tile build.
   */
  update(
    sunDir: THREE.Vector3, cameraAltitudeM: number, tau?: number | null, mayRunPass?: boolean,
  ): void;
  /**
   * Passes still owed, and one more while the beam tables are still building.
   *
   * The Atlas draws on demand, so a caller has to ask for another frame while
   * this is nonzero: one pass a frame is only amortised if there IS a next
   * frame, and a run that stops with the sky-view table invalid draws the sky
   * the sun was at two moves ago.
   */
  pending(): number;
  /**
   * What the air leaves of the sun's light, per channel, toward a sun whose
   * direction has `mu` for its local-zenith component: the transmittance
   * pass's own integral for that one ray, on the CPU, times what the cloud
   * deck leaves of it. The walkaround's sun disc is drawn in its hue.
   */
  sunTransmittance(mu: number, out: THREE.Vector3): THREE.Vector3;
  /** Merge into a consumer material's uniforms; `glsl` declares all of them. */
  readonly uniforms: Record<string, THREE.IUniform>;
  /** The sampling chunk consumers inject. Prepend to a fragment shader. */
  readonly glsl: string;
  /** What tells this row's chunk from another's: a program cache key that shares one program across bodies carries it. */
  readonly glslKey: string;
  stats(): AtmosphereStats;
  params(): AtmosphereParams;
  /** Run every pass still owed, now. Instruments only: it ignores the
   *  one-per-frame cap the frame budget is protected by. */
  flush(): void;
  readLut(name: string): AtmosphereLutRead | { ok: false; why: string };
  /**
   * Instruments only. Every knob here invalidates the tables, so the passes
   * the caller is about to measure are actually owed.
   *
   * `scaleHeightScale` is `atmo-check --gpu --self`'s deliberately wrong scale
   * height. `betaMScale` is `descent-dojo --haze --inject opaque`'s thicker
   * dust: it multiplies the aerosol extinction alone, so the shell keeps its
   * top - which `scaleHeightScale` moves, since `applyParams` derives `topM`
   * from the scale heights. `syncPasses` drains the GPU either side of each
   * pass so its `ms` is the pass's own cost and not its submit - it stalls the
   * pipeline, which is why it is not what the game runs. `injectPassMs`
   * busy-waits inside the pass, which is `descent-dojo --sky
   * --inject-pass-ms`'s known cost.
   */
  setDebug(next: {
    scaleHeightScale?: number | null;
    betaMScale?: number | null;
    syncPasses?: boolean;
    injectPassMs?: number | null;
    deckOff?: boolean;
    deckFlip?: boolean;
    /** `descent-dojo --veil --inject veil`: the binary per-sample sun test
     *  back in the aerial march, and nothing else changed with it. */
    hardSunShadow?: boolean;
    /** `descent-dojo --skyband`: every table back to the zeros a fresh mount
     *  starts from, so the passes that follow are all that is in them. */
    clearTables?: boolean;
    /** `descent-dojo --skyband --survey --survey-nan`: the next `update` reads a
     *  NaN sun, eye height or both, on a frame the sky-view table is owed a pass. */
    nonFiniteFrame?: 'altitude' | 'sun' | 'both' | null;
    /** `--inject sky-latch`: that NaN let through to the tables again. */
    nonFiniteGuardOff?: boolean;
    /** `descent-dojo --skyband --inject sky-band`: each pass sizes its viewport
     *  in canvas pixels again, which three scales by the pixel ratio. */
    canvasViewport?: boolean;
    /** `descent-dojo --sunset-sweep --inject sky-steps`: the sky drawn from the
     *  newest sky-view table alone, stepping as each is built. */
    skySteps?: boolean;
    /** `descent-dojo --sunset-sweep`'s streaming leg: every frame yields a pass
     *  owed as if the tile mounter had built on it. */
    tilesEveryFrame?: boolean;
  }): void;
  /**
   * Does the whole chunk compile, link and evaluate?
   *
   * The sky samples half of it and the tiles and the plot sample the other
   * half, so a syntax error or an undeclared uniform in either would surface
   * in a consumer's own build well before this would catch it; it stays as
   * the fast, isolated check that needs no mounted tile or plot to run.
   * Instruments only: it allocates and frees a 4x4 target.
   */
  probeGlsl(): { ok: boolean; why?: string; texel?: number[] };
  /**
   * `atmo-check --gpu`: `atmoAerialPerspective` and `atmoSkyRadiance` for the
   * SAME direction and sun, off the real GPU chunk - what the aerial term's
   * convergence at the horizon is measured against, rather than a re-typed
   * copy of the formula that could drift from what actually runs.
   *
   * @param dirLocal, sunLocal local-horizon frame, +y the zenith - the same
   *   convention `atmoAerialPerspective` and `atmoSkyRadiance` both take.
   */
  probeAerial(
    dirLocal: readonly [number, number, number],
    sunLocal: readonly [number, number, number],
    distanceM: number,
  ): AtmosphereAerialProbe | { ok: false; why: string };
  /**
   * `dust-sky-check.mjs`: the sky-view pass's own march (radiance plus the ground bounce) for up to 16 explicit
   * directions at the eye, without the 200x100 table's interpolation. Local frame, +y the zenith.
   */
  probeSky(
    dirsLocal: readonly (readonly [number, number, number])[],
    sunLocal: readonly [number, number, number],
  ): { ok: true; radiance: (readonly [number, number, number])[] } | { ok: false; why: string };
  dispose(): void;
}

export interface AtmosphereAerialProbe {
  readonly ok: true;
  readonly inScatter: readonly [number, number, number];
  readonly transmittance: readonly [number, number, number];
  readonly skyRadiance: readonly [number, number, number];
}

// ── the shader chunks ───────────────────────────────────────────────────────

/**
 * Parameters, mappings and the transmittance table's sampler.
 *
 * Every texel is addressed the way Bruneton addresses one: a pass writes texel
 * i at x = i / (n - 1), and a sampler reads x back at uv = 0.5/n + x (1 - 1/n).
 * The two are exact inverses, and mixing this convention with Hillaire's own
 * sub-uv one puts every read half a texel off the value that was written.
 */
const ATMO_COMMON_GLSL = /* glsl */ `
#define ATMO_PI 3.141592653589793
#define ATMO_MS_TERMS ${MULTI_SCATTER_TERMS}
#ifndef ATMO_BEAM
#define ATMO_BEAM 0
#endif

/**
 * Ceilings no scattering term may pass.
 *
 * Hillaire's series over the higher orders is 1/(1 - f_ms), and f_ms goes to 1
 * where the medium is thick AND conservative: Venus's sub-cloud column read it
 * inside the 1e-4 the denominator is clamped at, the factor ran to 1e4, and the
 * disc clipped to white at every band with the globe's own map invisible under
 * it. Each ceiling is the largest value its own term can physically take, and
 * the two blocks of the multiple-scattering table do not share one: the
 * atmosphere term is a phase-free field, which cannot exceed the whole solar
 * illuminance spread isotropically, 1/(4 pi); the ground term is a white
 * Lambertian bounce, which is 1/pi. One ceiling of 1/(4 pi) over both clipped
 * Mars's own ground block, whose peak is 0.1492 - and the sky-view table it
 * feeds moved with it.
 *
 * The in-scatter integral gets no constant at all, because the bound depends on
 * the RAY: what the path took out of the beam can come back as bright as the
 * phase function is peaked, and Mars's forward lobe is 9.1 per steradian at
 * zero scattering angle in red and 21 in blue where isotropic is 1/(4 pi). A
 * flat (1 - T)/pi was a third of what Mars's own aerial term reads toward the
 * sun. atmoInScatterMax below is the same bound taken per ray, from the phase
 * values the march itself uses.
 */
#define ATMO_MS_MAX 0.0795774715
#define ATMO_MS_GND_MAX 0.3183098862
/** Share of the in-scatter ceiling the soft knee is the identity below. */
#define ATMO_KNEE_START 0.8

uniform vec3 uAtmoBetaR;
uniform float uAtmoBetaM;
/** The aerosol's extinction per channel over uAtmoBetaM. */
uniform vec3 uAtmoMieExt;
/** Share of the aerosol's extinction that scatters, per channel. */
uniform vec3 uAtmoMieSsa;
/** (rayleigh, aerosol) scale heights, metres. */
uniform vec2 uAtmoScaleH;
/** (ground, top) radii, metres. */
uniform vec2 uAtmoRadii;
/** One Cornette-Shanks lobe's asymmetry: the phase on every row without mie_lobes. */
uniform float uAtmoMieG;
/** 1 where the row's mie_lobes replace that lobe with the three below. */
uniform float uAtmoMieLobes;
/** Per channel: the forward lobe's asymmetry, and its share of the scattering. */
uniform vec3 uAtmoMieG1;
uniform vec3 uAtmoMieAlpha;
/** The broad lobe's asymmetry. */
uniform float uAtmoMieG2;
/** The ground's reflectance the constant-albedo consumers bounce off it, per
 *  channel: on a body whose own ground is coloured, a grey number here is a
 *  grey sky over a red desert. */
uniform vec3 uAtmoGroundAlbedo;
/** The eye's white balance on the light the air scatters to it: 1 but under a
 *  column deeper than the calibration body's (src/atlas/exposure.ts). */
uniform vec3 uAtmoWhite;
/** Unit, local horizon frame: +y is the local zenith. */
uniform vec3 uAtmoSunDir;
/** Ground radius plus the camera's altitude CLAMPED into the shell, metres:
 *  the radius every table was built at, so a table read anywhere else is a
 *  table for somewhere else. */
uniform float uAtmoViewHeight;
/** Ground radius plus the camera's TRUE altitude, metres. Where the aerial
 *  march starts, and thousands of kilometres above uAtmoViewHeight at ORBIT. */
uniform float uAtmoEyeHeight;
uniform sampler2D uAtmoTransmittance;
uniform sampler2D uAtmoMultiScatter;
uniform sampler2D uAtmoSkyView;
/** The table built before uAtmoSkyView, and how far past uAtmoSkyView's sun
 *  height the sun is now, in units of the two tables' spacing (0: draw it alone). */
uniform sampler2D uAtmoSkyViewPrev;
uniform float uAtmoSkyViewExtrap;
uniform vec2 uAtmoTransmittanceSize;
uniform vec2 uAtmoMultiScatterSize;
uniform vec2 uAtmoSkyViewSize;
#if ATMO_BEAM
/** Q, the dust's scattering past single (src/atlas/dustTables.ts), zeros until its first build lands. */
${DUST_LOOK_GLSL}
/** The sky's irradiance on the ground per unit sun against its sun cosine, from the first MS pass; zero in that pass. */
uniform sampler2D uAtmoGroundDiffuse;
uniform float uAtmoGroundDiffuseOn;
vec3 atmoGroundDiffuse(float muSun) {
  float u = ${0.5 / GROUND_DIFFUSE_W} + clamp(muSun * 0.5 + 0.5, 0.0, 1.0) * ${1 - 1 / GROUND_DIFFUSE_W};
  return uAtmoGroundDiffuseOn * texture2D(uAtmoGroundDiffuse, vec2(u, 0.5)).rgb;
}
#endif

float atmoUnitToTexCoord(float x, float n) { return 0.5 / n + x * (1.0 - 1.0 / n); }

/**
 * The medium at height h: what scatters, and what the whole column takes out.
 *
 * The aerosol's scattering is a triple. Mars dust absorbs blue, and since it
 * carries 0.50 of the body's 0.503 column against the gas's 0.003, that
 * asymmetry IS the sky's colour - grey dust gives a grey sky whatever the
 * Rayleigh triple says. Its extinction is grey (uAtmoMieExt 1) on every row
 * but Titan's, which is what leaves Mars's transmittance table exactly the
 * integral atmo-check --gpu re-derives from bodies.json.
 */
void atmoMedium(float h, out vec3 scatterR, out vec3 scatterM, out vec3 extinction) {
  scatterR = uAtmoBetaR * exp(-h / uAtmoScaleH.x);
  vec3 extM = uAtmoBetaM * uAtmoMieExt * exp(-h / uAtmoScaleH.y);
  scatterM = uAtmoMieSsa * extM;
  extinction = scatterR + extM;
}

float atmoRayleighPhase(float c) { return 3.0 / (16.0 * ATMO_PI) * (1.0 + c * c); }

vec3 atmoHenyeyGreenstein(vec3 g, float c) {
  return (1.0 - g * g) / (4.0 * ATMO_PI * pow(max(1.0 + g * g - 2.0 * g * c, vec3(1e-4)), vec3(1.5)));
}

/** Cornette-Shanks, which is what Hillaire's dust and haze use, or the row's measured two lobes. */
vec3 atmoMiePhase(float c) {
  if (uAtmoMieLobes > 0.5) {
    return uAtmoMieAlpha * atmoHenyeyGreenstein(uAtmoMieG1, c)
      + (vec3(1.0) - uAtmoMieAlpha) * atmoHenyeyGreenstein(vec3(uAtmoMieG2), c);
  }
  float g = uAtmoMieG;
  float k = 3.0 / (8.0 * ATMO_PI) * (1.0 - g * g) / (2.0 + g * g);
  return vec3(k * (1.0 + c * c) / pow(max(1.0 + g * g - 2.0 * g * c, 1e-4), 1.5));
}

/**
 * The most in-scatter a march can hold, per unit sun illuminance.
 *
 * Along a straight ray the phase values are constants, and the source at every
 * sample is at most the extinction times (the larger of the two phase values,
 * since the two scattering coefficients sum to no more than the extinction)
 * plus the multiple-scattering table's own two ceilings. Integrated against
 * exp(-tau) that is exactly (1 - T) times the bracket.
 */
vec3 atmoInScatterMax(vec3 throughput, float phaseR, vec3 phaseM, vec3 groundAlbedo) {
  return (vec3(1.0) - throughput)
    * (max(vec3(phaseR), phaseM) + ATMO_MS_MAX + groundAlbedo * ATMO_MS_GND_MAX);
}

/**
 * The same ceiling, approached instead of clipped against.
 *
 * A min against the bound is a hard clip, and a clip has no slope: every ray
 * whose raw integral passes the bound reads the SAME value, so the brightest
 * part of a twilight wedge draws as one constant colour with a cliff at its
 * edge and the ground stops showing through it at all. Below the knee this is
 * the identity and its slope there is 1, so nothing that was not already near
 * the bound moves by anything, and the result never exceeds the bound - which
 * is what makes it a legal substitute for the min.
 */
vec3 atmoInScatterKnee(vec3 v, vec3 ceiling) {
  vec3 k = ceiling * ATMO_KNEE_START;
  vec3 over = max(v - k, vec3(0.0));
  vec3 room = max(ceiling - k, vec3(1e-12));
  return min(v, k) + room * (over / (over + room));
}

/** Nearest hit ahead of the origin with a sphere of that radius at the origin, or -1. */
float atmoRaySphere(vec3 o, vec3 d, float radius) {
  float b = dot(o, d);
  float c = dot(o, o) - radius * radius;
  float disc = b * b - c;
  if (disc < 0.0) return -1.0;
  float s = sqrt(disc);
  float t0 = -b - s;
  float t1 = -b + s;
  if (t1 < 0.0) return -1.0;
  return t0 < 0.0 ? t1 : t0;
}

/** Both roots of the sphere of that radius at the origin, near first, or false. */
bool atmoRaySphereSpan(vec3 o, vec3 d, float radius, out float tNear, out float tFar) {
  float b = dot(o, d);
  float c = dot(o, o) - radius * radius;
  float disc = b * b - c;
  if (disc < 0.0) return false;
  float s = sqrt(disc);
  tNear = -b - s;
  tFar = -b + s;
  return true;
}

/** Distance from r along mu to the top of the shell, ignoring the ground. */
float atmoDistanceToTop(float r, float mu) {
  float rt = uAtmoRadii.y;
  return max(-r * mu + sqrt(max(r * r * (mu * mu - 1.0) + rt * rt, 0.0)), 0.0);
}

void atmoTransmittanceUvToParams(vec2 x, out float r, out float mu) {
  float rg = uAtmoRadii.x;
  float rt = uAtmoRadii.y;
  float H = sqrt(max(rt * rt - rg * rg, 0.0));
  float rho = H * x.y;
  r = sqrt(rho * rho + rg * rg);
  float dMin = rt - r;
  float dMax = rho + H;
  float d = dMin + x.x * (dMax - dMin);
  mu = d <= 0.0 ? 1.0 : clamp((H * H - rho * rho - d * d) / (2.0 * r * d), -1.0, 1.0);
}

vec2 atmoTransmittanceParamsToUv(float r, float mu) {
  float rg = uAtmoRadii.x;
  float rt = uAtmoRadii.y;
  float H = sqrt(max(rt * rt - rg * rg, 0.0));
  float rho = sqrt(max(r * r - rg * rg, 0.0));
  float d = atmoDistanceToTop(r, mu);
  float dMin = rt - r;
  float dMax = rho + H;
  return vec2(clamp((d - dMin) / max(dMax - dMin, 1e-4), 0.0, 1.0), clamp(rho / max(H, 1e-4), 0.0, 1.0));
}

/** Where (r, mu) reads the transmittance table, and every table laid on its grid. */
vec2 atmoTransmittanceUv(float r, float mu) {
  vec2 x = atmoTransmittanceParamsToUv(clamp(r, uAtmoRadii.x, uAtmoRadii.y), mu);
  return vec2(atmoUnitToTexCoord(x.x, uAtmoTransmittanceSize.x),
              atmoUnitToTexCoord(x.y, uAtmoTransmittanceSize.y));
}

/** The transmittance texel at (r, mu): alpha is the aerosol column the beam tables are indexed by. */
vec4 atmoTransmittanceTexel(float r, float mu) {
  return texture2D(uAtmoTransmittance, atmoTransmittanceUv(r, mu));
}

/** Transmittance from (r, mu) to the top of the shell. */
vec3 atmoTransmittanceToTop(float r, float mu) {
  return atmoTransmittanceTexel(r, mu).rgb;
}

/** The Rayleigh column from the aerosol one: exact where the two scale heights match, as on Mars. */
vec3 atmoRayleighColumn(float tauM) {
  return uAtmoBetaR * (tauM / max(uAtmoBetaM, 1e-30));
}

/** The sun's transmittance with the forward lobe's scattering left in the beam (delta-M, f = alpha) on a row with mie_lobes. */
vec3 atmoSunTransmittanceScaled(float r, float mu) {
  vec4 t = atmoTransmittanceTexel(r, mu);
  if (uAtmoMieLobes < 0.5) return t.rgb;
  return exp(-atmoRayleighColumn(t.a) - t.a * uAtmoMieExt * (vec3(1.0) - uAtmoMieSsa * uAtmoMieAlpha));
}

/**
 * The two multiple-scattering terms at (r, cos sun zenith).
 *
 * atmTerm is Psi_ms with the ground black and gndTerm is the albedo-1 ground
 * bounce, so Psi_ms for any albedo is atmTerm + albedo * gndTerm. Two
 * texture2D of one texture rather than two samplers - see MULTI_SCATTER_TERMS.
 */
void atmoMultiScatterTerms(float r, float muSun, out vec3 atmTerm, out vec3 gndTerm) {
  float t = clamp((r - uAtmoRadii.x) / max(uAtmoRadii.y - uAtmoRadii.x, 1.0), 0.0, 1.0);
  float u = atmoUnitToTexCoord(clamp(muSun * 0.5 + 0.5, 0.0, 1.0), uAtmoMultiScatterSize.x)
    / float(ATMO_MS_TERMS);
  float v = atmoUnitToTexCoord(t, uAtmoMultiScatterSize.y);
  atmTerm = texture2D(uAtmoMultiScatter, vec2(u, v)).rgb;
  gndTerm = texture2D(uAtmoMultiScatter, vec2(u + 1.0 / float(ATMO_MS_TERMS), v)).rgb;
}

/** The same factor at the body's own mean albedo, which is what every
 *  consumer that is not a terrain fragment reads. */
vec3 atmoMultiScatter(float r, float muSun) {
  vec3 atmTerm;
  vec3 gndTerm;
  atmoMultiScatterTerms(r, muSun, atmTerm, gndTerm);
  return atmTerm + uAtmoGroundAlbedo * gndTerm;
}

/** Hillaire's horizon-centred sky-view mapping, and its inverse. */
void atmoSkyViewUvToParams(vec2 x, float r, out float muView, out float lightViewCos) {
  float rg = uAtmoRadii.x;
  float beta = acos(clamp(sqrt(max(r * r - rg * rg, 0.0)) / r, -1.0, 1.0));
  float zenithHorizon = ATMO_PI - beta;
  float zen;
  if (x.y < 0.5) {
    float c = 1.0 - 2.0 * x.y;
    zen = zenithHorizon * (1.0 - c * c);
  } else {
    float c = x.y * 2.0 - 1.0;
    zen = zenithHorizon + beta * c * c;
  }
  muView = cos(zen);
  lightViewCos = 1.0 - 2.0 * x.x * x.x;
}

vec2 atmoSkyViewParamsToUv(float r, float muView, float lightViewCos, bool hitsGround) {
  float rg = uAtmoRadii.x;
  float beta = acos(clamp(sqrt(max(r * r - rg * rg, 0.0)) / r, -1.0, 1.0));
  float zenithHorizon = ATMO_PI - beta;
  float zen = acos(clamp(muView, -1.0, 1.0));
  float y;
  if (hitsGround) {
    y = sqrt(clamp((zen - zenithHorizon) / max(beta, 1e-4), 0.0, 1.0)) * 0.5 + 0.5;
  } else {
    y = (1.0 - sqrt(clamp(1.0 - zen / max(zenithHorizon, 1e-4), 0.0, 1.0))) * 0.5;
  }
  return vec2(sqrt(clamp((1.0 - lightViewCos) * 0.5, 0.0, 1.0)), y);
}
`;

/**
 * The single-scattering integrator both the multiple-scattering and sky-view
 * passes run.
 *
 * `ATMO_STEPS` is a define rather than an argument because GLSL ES 1.00 needs a
 * constant loop bound, so each pass sets its own before including this. The
 * other two are defines for reasons of their own:
 *
 * `ATMO_MS_APPROX` gates the sample of the multiple-scattering table. A runtime
 * `if` would leave that sampler statically referenced by the pass that RENDERS
 * into it, which is a feedback loop a driver is free to resolve by dropping the
 * draw - and a target that keeps its clear value passes any gate that only asks
 * whether the values are finite.
 *
 * `ATMO_UNIFORM_PHASE` integrates with 1/(4 pi) instead of the real phase.
 * Hillaire's second-order estimate is a phase-free factor, so the pass that
 * builds it takes the uniform phase; Mars's forward lobe peaks at 9.1 to 21
 * against the uniform 0.0796, and a 6x6 direction lattice sampling that
 * spike biases the factor along the table's own sun axis.
 */
const ATMO_INTEGRATE_GLSL = /* glsl */ `
struct AtmoScatter {
  /** Everything but the ground bounce: the integral with the ground BLACK. */
  vec3 radiance;
  /** The ground bounce at albedo 1, attenuated back to the start of the ray.
   *  The only place the ground enters, and linear in albedo, so a consumer
   *  reads radiance + A * groundBounceAs1 for whatever albedo it wants. */
  vec3 groundBounceAs1;
  /** The same integral with scattering albedo 1 and no phase: the factor
   *  Hillaire's geometric series over higher scattering orders is taken on. */
  vec3 multiScatterAs1;
};

AtmoScatter atmoIntegrate(vec3 pos, vec3 dir, vec3 sunDir, bool groundBounce) {
  AtmoScatter res;
  res.radiance = vec3(0.0);
  res.groundBounceAs1 = vec3(0.0);
  res.multiScatterAs1 = vec3(0.0);
  float rg = uAtmoRadii.x;
  float tTop = atmoRaySphere(pos, dir, uAtmoRadii.y);
  float tGround = atmoRaySphere(pos, dir, rg);
  float tMax;
  if (tGround < 0.0) {
    if (tTop < 0.0) return res;
    tMax = tTop;
  } else {
    tMax = tTop > 0.0 ? min(tTop, tGround) : tGround;
  }
  if (tMax <= 0.0) return res;

  float dt = tMax / float(ATMO_STEPS);
  float c = dot(dir, sunDir);
#if ATMO_UNIFORM_PHASE
  float phaseR = 1.0 / (4.0 * ATMO_PI);
  vec3 phaseM = vec3(phaseR);
#else
  float phaseR = atmoRayleighPhase(c);
  vec3 phaseM = atmoMiePhase(c);
#endif
  vec3 throughput = vec3(1.0);
#if ATMO_UNIFORM_PHASE
  // Delta-M on a row with mie_lobes: the forward lobe's scattering stays in the beam. 0 elsewhere.
  vec3 forwardShare = uAtmoMieLobes > 0.5 ? uAtmoMieAlpha : vec3(0.0);
#endif
  for (int i = 0; i < ATMO_STEPS; i++) {
    vec3 p = pos + dir * ((float(i) + 0.5) * dt);
    float r = max(length(p), rg);
    vec3 scatterR; vec3 scatterM; vec3 ext;
    atmoMedium(r - rg, scatterR, scatterM, ext);
#if ATMO_UNIFORM_PHASE
    ext -= forwardShare * scatterM;
#endif
    vec3 segment = exp(-ext * dt);
    vec3 up = p / r;
    float muSun = dot(up, sunDir);
    float lit = atmoRaySphere(p, sunDir, rg) < 0.0 ? 1.0 : 0.0;
    vec3 safeExt = max(ext, vec3(1e-14));
#if ATMO_UNIFORM_PHASE
    vec3 scattering = scatterR + scatterM * (vec3(1.0) - forwardShare);
    // The source with the forward lobe's share left in the beam, as the extinction above leaves it.
    vec3 s = lit * atmoSunTransmittanceScaled(r, muSun)
      * (scatterR * phaseR + scatterM * (vec3(1.0) - forwardShare) * phaseM);
#if ATMO_MS_APPROX
    s += atmoMultiScatter(r, muSun) * scattering;
#endif
#else
    vec3 scattering = scatterR + scatterM;
    vec3 s = lit * atmoTransmittanceToTop(r, muSun) * (scatterR * phaseR + scatterM * phaseM);
#if ATMO_BEAM
    if (lit > 0.0) s += exp(-(r - rg) / uAtmoScaleH.y) * dqQ(p, dir, sunDir);
#if ATMO_MS_APPROX
    s += atmoMultiScatter(r, muSun) * (scatterR + scatterM * (vec3(1.0) - uAtmoMieAlpha));
#endif
#elif ATMO_MS_APPROX
    s += atmoMultiScatter(r, muSun) * scattering;
#endif
#endif
    res.radiance += throughput * ((s - s * segment) / safeExt);
    res.multiScatterAs1 += throughput * ((scattering - scattering * segment) / safeExt);
    throughput *= segment;
  }
  if (groundBounce && tGround > 0.0 && tMax == tGround) {
    vec3 p = pos + dir * tGround;
    float r = max(length(p), rg);
    float nDotL = clamp(dot(p / r, sunDir), 0.0, 1.0);
    res.groundBounceAs1 += throughput * nDotL * (1.0 / ATMO_PI)
      * atmoSunTransmittanceScaled(r, nDotL);
#if ATMO_BEAM && ATMO_UNIFORM_PHASE
    res.groundBounceAs1 += throughput * (1.0 / ATMO_PI) * atmoGroundDiffuse(dot(p / r, sunDir));
#endif
  }
  res.radiance = min(res.radiance,
    atmoInScatterMax(throughput, phaseR, phaseM, uAtmoGroundAlbedo));
  return res;
}
`;

/**
 * What consumers inject: the sky along a view ray, and the aerial-perspective
 * pair for a surface at a distance.
 *
 * Both take directions in the LOCAL HORIZON frame, +y the local zenith at the
 * camera's ground point, because that is the only frame the tables are indexed
 * in. The sky-view table folds the sun's azimuth onto +x, so what reaches it is
 * the view's azimuth RELATIVE to the sun's, which is what `lightViewCos` is.
 */
const ATMO_SAMPLE_GLSL = /* glsl */ `
#define ATMO_AERIAL_STEPS ${AERIAL_STEPS}

/** descent-dojo --veil --inject veil: the binary per-sample sun test back. */
uniform float uAtmoHardSun;

/**
 * How much of the sun a sample at p can see: 0 in the body's own shadow, 1 in
 * full light, graded across the terminator.
 *
 * A yes/no ground-occlusion test takes a sample from unlit to fully lit in one
 * pixel, and the eight samples of the aerial march sit at heights from the
 * ground to the top of the shell - so each one flips at its OWN place, and a
 * sample 64 km up on Earth stays lit 845 km of ground arc past the sample on
 * the ground beside it. The twilight wedge then draws as up to eight hard
 * arcs, which at ORBIT is the band across the disc and the nested arcs over
 * the night side the owner reported.
 *
 * What the grade is wide enough to cover is the march's own resolution rather
 * than the sun's angular width: the whole height span subtends
 * sqrt(2 (rTop - rg) / rg) of terminator offset in muSun, shared among the
 * steps, and the sun's own 0.27 degrees at Earth is a tenth of one step of
 * that. Under this width there is nothing the eight samples can resolve, and
 * drawing detail finer than the sampling IS the artifact.
 *
 * One-sided on purpose. Centring the grade on the geometric horizon lights
 * samples that are inside the body's own umbra, which is light out of nowhere:
 * measured, it doubled the twilight limb over the night side. So the ramp
 * STARTS at the horizon and this can only ever return less than the yes/no
 * test did, never more.
 */
float atmoSunVisibility(vec3 p, vec3 sunDir) {
  float rg = uAtmoRadii.x;
  float r = max(length(p), rg);
  float sinH = rg / r;
  float cosH = -sqrt(max(1.0 - sinH * sinH, 0.0));
  float muSun = dot(p / r, sunDir);
  if (uAtmoHardSun > 0.5) return muSun > cosH ? 1.0 : 0.0;
  float span = sqrt(2.0 * max(uAtmoRadii.y - rg, 1.0) / rg);
  float w = 2.0 * span / float(ATMO_AERIAL_STEPS);
  return smoothstep(cosH, cosH + w, muSun);
}

/** (base, top) radii of the opaque cloud deck, metres. Both 0 where none. */
uniform vec2 uAtmoDeckRadii;
/** Extinction inside that slab, per metre. 0 where the body has no deck. */
uniform float uAtmoDeckExt;
/** The slab's own radiance per unit sun illuminance, before its own muSun:
 *  the cloud top's reflectance over pi seen from outside, and the share of the
 *  incident flux it passes over pi seen from under it. */
uniform vec3 uAtmoDeckRadiance;

/**
 * The opaque cloud deck along dir: what it leaves of whatever is behind it, and
 * what it puts in front.
 *
 * 05-atmosphere-and-light.md, "Scattering": the LUT terminates at the deck, so
 * from outside the surface is never visible and from the surface there is no
 * sun disc. The LUT terminating is half of that; this is the other half, the
 * slab ITSELF, and it is what gives the descent a fog to cross. At Venus's 70
 * km the path through it is 22 km at tau 30 and nothing behind it survives; at
 * 49 km it is one kilometre and the ground comes back. Which of its two faces
 * the eye is on is uAtmoDeckRadiance's own job, set once a frame.
 *
 * A uniform slab and not an exponential: the registry's aerosol scale height is
 * a surface-hugging stand-in for cloud that is kilometres up, and the one
 * number canon publishes for the deck is a column optical depth.
 */
vec3 atmoDeck(vec3 eye, vec3 dir, vec3 sunDir, float distanceM, out float deckT) {
  deckT = 1.0;
  if (uAtmoDeckExt <= 0.0) return vec3(0.0);
  float maxT = max(distanceM, 0.0);
  float tn;
  float tf;
  if (!atmoRaySphereSpan(eye, dir, uAtmoDeckRadii.y, tn, tf) || tf <= 0.0) return vec3(0.0);
  float t0 = clamp(tn, 0.0, maxT);
  float t1 = clamp(tf, 0.0, maxT);
  // The NEAR wall only. A chord through the whole top sphere re-enters the slab
  // on the far side of the body, where nothing drawn is behind it, and its
  // midpoint lands at the body's own centre - which is a normalize of zero and
  // was one flat wrong muSun over the whole disc.
  if (atmoRaySphereSpan(eye, dir, uAtmoDeckRadii.x, tn, tf) && tf > 0.0) {
    if (tn > 0.0) t1 = min(t1, clamp(tn, 0.0, maxT));
    else t0 = max(t0, clamp(tf, 0.0, maxT));
  }
  if (t1 <= t0) return vec3(0.0);
  deckT = exp(-uAtmoDeckExt * (t1 - t0));
  // Through the whole slab the light is diffuse, lit by the sun over the eye's
  // own column. Above it the cloud seen is where the ray enters: a grazing
  // chord's midpoint is hundreds of km on, and at tau 30 none of its light arrives.
  vec3 lit = length(eye) < uAtmoDeckRadii.y ? eye : eye + dir * t0;
  float muSun = clamp(dot(normalize(lit), sunDir), 0.0, 1.0);
  return uAtmoDeckRadiance * muSun * (1.0 - deckT);
}

/** Sky radiance along dir, per unit sun illuminance. */
vec3 atmoSkyRadiance(vec3 dir, vec3 sunDir) {
  float r = clamp(uAtmoViewHeight, uAtmoRadii.x + 1.0, uAtmoRadii.y - 1.0);
  vec3 up = vec3(0.0, 1.0, 0.0);
  float muView = clamp(dir.y, -1.0, 1.0);
  vec3 dh = dir - up * muView;
  vec3 sh = sunDir - up * sunDir.y;
  float lvc = (length(dh) < 1e-5 || length(sh) < 1e-5) ? 1.0 : dot(normalize(dh), normalize(sh));
  bool hitsGround = atmoRaySphere(vec3(0.0, r, 0.0), dir, uAtmoRadii.x) > 0.0;
  vec2 x = atmoSkyViewParamsToUv(r, muView, clamp(lvc, -1.0, 1.0), hitsGround);
  vec2 uv = vec2(atmoUnitToTexCoord(x.x, uAtmoSkyViewSize.x),
                 atmoUnitToTexCoord(x.y, uAtmoSkyViewSize.y));
  vec3 sky = texture2D(uAtmoSkyView, uv).rgb;
  if (uAtmoSkyViewExtrap != 0.0) {
    sky = max(sky + (sky - texture2D(uAtmoSkyViewPrev, uv).rgb) * uAtmoSkyViewExtrap, vec3(0.0));
  }
  return sky;
}

/**
 * What the atmosphere leaves of whatever is behind it along dir.
 *
 * The sky consumer's alpha: one minus this is how much of the stars, or of the
 * pane's clear colour, the air in front of them takes out. dir is in the same
 * local horizon frame atmoSkyRadiance takes, and a ray that ends on the
 * ground rather than at the top of the shell reads the grazing value, which is
 * the table's own edge - those fragments are terrain and are drawn over.
 */
vec3 atmoViewTransmittance(vec3 dir) {
  float r = clamp(uAtmoViewHeight, uAtmoRadii.x + 1.0, uAtmoRadii.y - 1.0);
  return atmoTransmittanceToTop(r, clamp(dir.y, -1.0, 1.0));
}

/**
 * Aerial perspective over distanceM along dir.
 *
 * transmittance is what the surface's own colour is multiplied by and
 * inScatter is what is added in front of it, both per unit sun illuminance.
 *
 * Only the part of that distance INSIDE the shell is marched. An ORBIT eye is
 * thousands of kilometres above a top at eight scale heights, so a march from
 * the eye over the whole eye-to-ground distance put every one of its samples
 * under the ground - where max(length(p), rg) reads them at surface density -
 * and 8,000 km of that is an optical depth in the hundreds. Transmittance came
 * back 0 at the disc's centre and the convergence blend below then painted the
 * whole disc the sky's own colour, which is what the fault looked like.
 */
void atmoAerialPerspective(
  vec3 dir, vec3 sunDir, float distanceM, vec3 groundAlbedo, bool endsOnGround,
  out vec3 transmittance, out vec3 inScatter
) {
  transmittance = vec3(1.0);
  inScatter = vec3(0.0);
  float rg = uAtmoRadii.x;
  vec3 eye = vec3(0.0, max(uAtmoEyeHeight, rg), 0.0);
  float tEnter;
  float tExit;
  if (!atmoRaySphereSpan(eye, dir, uAtmoRadii.y, tEnter, tExit)) return;
  float t0 = max(tEnter, 0.0);
  float t1 = min(max(distanceM, 0.0), tExit);
  if (t1 <= t0) return;
  float dt = (t1 - t0) / float(ATMO_AERIAL_STEPS);
  float c = dot(dir, sunDir);
  float phaseR = atmoRayleighPhase(c);
  vec3 phaseM = atmoMiePhase(c);
  for (int i = 0; i < ATMO_AERIAL_STEPS; i++) {
    vec3 p = eye + dir * (t0 + (float(i) + 0.5) * dt);
    float r = max(length(p), rg);
    vec3 scatterR; vec3 scatterM; vec3 ext;
    atmoMedium(r - rg, scatterR, scatterM, ext);
    vec3 segment = exp(-ext * dt);
    vec3 up = p / r;
    float muSun = dot(up, sunDir);
    float lit = atmoSunVisibility(p, sunDir);
    vec3 scattering = scatterR + scatterM;
    vec3 msAtm;
    vec3 msGnd;
    atmoMultiScatterTerms(r, muSun, msAtm, msGnd);
    vec3 safeExt = max(ext, vec3(1e-14));
#if ATMO_BEAM
    vec3 s = (msAtm + groundAlbedo * msGnd) * (scatterR + scatterM * (vec3(1.0) - uAtmoMieAlpha));
    if (lit > 0.0) {
      s += lit * (atmoTransmittanceToTop(r, muSun) * (scatterR * phaseR + scatterM * phaseM)
        + exp(-(r - rg) / uAtmoScaleH.y) * dqQ(p, dir, sunDir));
    }
    inScatter += transmittance * ((s - s * segment) / safeExt);
#else
    vec3 s = lit * atmoTransmittanceToTop(r, muSun) * (scatterR * phaseR + scatterM * phaseM)
      + (msAtm + groundAlbedo * msGnd) * scattering;
    inScatter += transmittance * ((s - s * segment) / safeExt);
#endif
    transmittance *= segment;
  }
  // What the path took out of the beam is all it can give back, and no more
  // brightly than its own phase function points it. Reached through a knee and
  // not a min, so the wedge that passes the bound keeps its slope.
  inScatter = atmoInScatterKnee(
    inScatter, atmoInScatterMax(transmittance, phaseR, phaseM, groundAlbedo));
  // Converge to the sky-view table's own radiance as the path hazes out, on a
  // ray that MISSES the ground and nothing else. endsOnGround is the
  // consumer's own word for that: atmoRaySphere answers whether an idealised
  // smooth sphere is in the way, which degenerates to the eye's horizontal
  // plane once uAtmoEyeHeight clamps to rg below datum, so a terrain
  // fragment standing on real relief above that plane used to read the miss
  // branch and get mixed to the sky regardless of what it stood on.
  //
  // The tail exists because a ray whose march stopped short of the top of the
  // shell holds a partial integral, while the sky beside it holds the whole
  // one, and the two read different totals where nothing should look like a
  // step. A ray that ENDS ON THE GROUND is not that case: its march is
  // complete at the fragment, and the sky table is the wrong quantity for it
  // twice over. It carries uAtmoGroundAlbedo times groundBounceAs1 - a second
  // copy of the ground's own reflected light, at one constant albedo for the
  // whole planet, on top of the copy the consumer adds as outgoingLight * T -
  // and it is indexed at uAtmoViewHeight, clamped INTO the shell, so from
  // orbit its near-tangent value is a 94 km path where the eye's own ray is a
  // 9,000 km grazing chord. Measured at the ORBIT nadir: 0.3213 of the pixel's
  // in-scatter came from the table and 27.4 percent of it was that duplicate
  // ground, which is what held a core pixel's albedo-tracking share near 30
  // percent. Continuous across the silhouette by construction rather than by a
  // ramp: a ray that just misses the ground marches the whole chord and
  // therefore already equals the table's value, which is what atmo-check
  // --gpu's convergence arm measures, so the weight falling to zero on the
  // inside of the silhouette is a no-op right where the test flips. The step
  // that IS left at the skyline is the render distance - the last drawn tile
  // is tens of kilometres out where the true horizon is hundreds - and
  // descent-dojo --sky's ungated horizon reading is where it shows.
  float fxTail = endsOnGround ? 0.0 : (atmoRaySphere(eye, dir, rg) > 0.0 ? 0.0 : 1.0);
  inScatter = mix(inScatter, atmoSkyRadiance(dir, sunDir),
    (vec3(1.0) - transmittance) * fxTail) * uAtmoWhite;
}

/** The pre-endsOnGround albedo overload: forwards false, so a call site
 *  written before that argument existed keeps reading the sphere gate. */
void atmoAerialPerspective(
  vec3 dir, vec3 sunDir, float distanceM, vec3 groundAlbedo,
  out vec3 transmittance, out vec3 inScatter
) {
  atmoAerialPerspective(
    dir, sunDir, distanceM, groundAlbedo, false, transmittance, inScatter);
}

/** The body's own ground reflectance, endsOnGround exposed: what a terrain
 *  fragment with no albedo of its own to pass reads. */
void atmoAerialPerspective(
  vec3 dir, vec3 sunDir, float distanceM, bool endsOnGround,
  out vec3 transmittance, out vec3 inScatter
) {
  atmoAerialPerspective(
    dir, sunDir, distanceM, uAtmoGroundAlbedo, endsOnGround, transmittance, inScatter);
}

/** The same march at the body's own ground reflectance: what every consumer
 *  that is not a terrain fragment reads. */
void atmoAerialPerspective(
  vec3 dir, vec3 sunDir, float distanceM, out vec3 transmittance, out vec3 inScatter
) {
  atmoAerialPerspective(
    dir, sunDir, distanceM, uAtmoGroundAlbedo, transmittance, inScatter);
}
`;

/** What a consumer prepends to its fragment shader. */
const ATMO_SAMPLING_CHUNK = `${ATMO_COMMON_GLSL}\n${ATMO_SAMPLE_GLSL}`;

const FULLSCREEN_VERT = /* glsl */ `
void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

/** What the probe's own fragment cannot sum to less than if it ran at all. */
const GLSL_PROBE_MIN_SUM = 0.5;

/** Calls every function the chunk exports, so linking is not the whole test. */
const GLSL_PROBE_FRAG = /* glsl */ `
${ATMO_SAMPLING_CHUNK}
void main() {
  vec3 dir = normalize(vec3(0.3, 0.5, 0.2));
  vec3 transmittance;
  vec3 inScatter;
  atmoAerialPerspective(dir, uAtmoSunDir, 2000.0, transmittance, inScatter);
  gl_FragColor = vec4(atmoSkyRadiance(dir, uAtmoSunDir) + inScatter + transmittance
    + atmoViewTransmittance(dir), 1.0);
}
`;

/**
 * `probeAerial`'s own uniforms and fragment: `atmoAerialPerspective` and
 * `atmoSkyRadiance` for an explicit direction, sun and distance rather than
 * the live camera's, so the reading is deterministic regardless of where the
 * Atlas is pointed when the instrument asks. A 3x1 target rather than three
 * separate passes: one draw, three texels, selected by `gl_FragCoord.x`.
 */
const PROBE_AERIAL_FRAG = /* glsl */ `
${ATMO_SAMPLING_CHUNK}
uniform vec3 uProbeDir;
uniform vec3 uProbeSun;
uniform float uProbeDist;
void main() {
  vec3 dir = normalize(uProbeDir);
  vec3 sun = normalize(uProbeSun);
  vec3 transmittance;
  vec3 inScatter;
  atmoAerialPerspective(dir, sun, uProbeDist, transmittance, inScatter);
  vec3 sky = atmoSkyRadiance(dir, sun);
  float x = gl_FragCoord.x;
  if (x < 1.0) gl_FragColor = vec4(inScatter, 1.0);
  else if (x < 2.0) gl_FragColor = vec4(transmittance, 1.0);
  else gl_FragColor = vec4(sky, 1.0);
}
`;

const TRANSMITTANCE_FRAG = /* glsl */ `
${ATMO_COMMON_GLSL}
#define ATMO_TAU_STEPS ${TRANSMITTANCE_STEPS}

/** Trapezoid over the unobstructed path to the top of the shell; tauM is the aerosol's column at uAtmoBetaM. */
vec3 atmoOpticalDepthToTop(float r, float mu, out float tauM) {
  float rg = uAtmoRadii.x;
  float d = atmoDistanceToTop(r, mu);
  float dx = d / float(ATMO_TAU_STEPS);
  vec3 sum = vec3(0.0);
  tauM = 0.0;
  for (int i = 0; i <= ATMO_TAU_STEPS; i++) {
    float t = float(i) * dx;
    float ri = sqrt(max(t * t + 2.0 * r * mu * t + r * r, rg * rg));
    vec3 scatterR; vec3 scatterM; vec3 ext;
    atmoMedium(ri - rg, scatterR, scatterM, ext);
    float w = (i == 0 || i == ATMO_TAU_STEPS) ? 0.5 : 1.0;
    sum += ext * w;
    tauM += uAtmoBetaM * exp(-(ri - rg) / uAtmoScaleH.y) * w;
  }
  tauM *= dx;
  return sum * dx;
}

void main() {
  vec2 x = (gl_FragCoord.xy - 0.5) / (uAtmoTransmittanceSize - 1.0);
  float r, mu;
  atmoTransmittanceUvToParams(x, r, mu);
  float tauM;
  vec3 depth = atmoOpticalDepthToTop(r, mu, tauM);
  gl_FragColor = vec4(exp(-depth), tauM);
}
`;

const MULTI_SCATTER_FRAG = /* glsl */ `
${ATMO_COMMON_GLSL}
#define ATMO_STEPS ${MULTI_SCATTER_STEPS}
// This pass RENDERS the multiple-scattering table, so it must not sample it,
// and the factor it builds is phase-free.
#define ATMO_MS_APPROX 0
#define ATMO_UNIFORM_PHASE 1
${ATMO_INTEGRATE_GLSL}
#define ATMO_MS_SQRT ${MULTI_SCATTER_SQRT_DIRS}
/** 1 where the ceiling scales the whole triple rather than clipping each channel. */
uniform float uAtmoMsKeepHue;
/**
 * 1 where the first row step is optically thick and no deck caps the column.
 * The ground row sits a metre up, where half its directions end on the ground
 * at once; under Titan's haze that read it 30x under the row 16 km up, and the
 * horizon, which samples only the ground row, drew 44x under the zenith.
 */
uniform float uAtmoMsHoldGround;

void main() {
  // The target is ATMO_MS_TERMS blocks of uAtmoMultiScatterSize.x columns side
  // by side, so (r, muSun) comes from the column INSIDE the block: both blocks
  // of a column run the same march and each writes its own term out of it.
  float col = gl_FragCoord.x - 0.5;
  float term = floor(col / uAtmoMultiScatterSize.x);
  float rowIndex = gl_FragCoord.y - 0.5;
  if (uAtmoMsHoldGround > 0.5) rowIndex = max(rowIndex, 1.0);
  vec2 x = vec2(col - term * uAtmoMultiScatterSize.x, rowIndex)
    / (uAtmoMultiScatterSize - 1.0);
  float muSun = x.x * 2.0 - 1.0;
  // A metre off the ground sphere, the way the sky-view pass clamps its own
  // view height: at r exactly rGround every direction's ground test returns
  // t = 0 and the integral collapses to nothing.
  float r = max(uAtmoRadii.x + x.y * (uAtmoRadii.y - uAtmoRadii.x), uAtmoRadii.x + 1.0);
  vec3 pos = vec3(0.0, r, 0.0);
  vec3 sunDir = normalize(vec3(sqrt(max(1.0 - muSun * muSun, 0.0)), muSun, 0.0));
  vec3 lSum = vec3(0.0);
  vec3 gSum = vec3(0.0);
  vec3 msSum = vec3(0.0);
  for (int i = 0; i < ATMO_MS_SQRT; i++) {
    for (int j = 0; j < ATMO_MS_SQRT; j++) {
      // Uniform on the sphere, not on the hemisphere: the factor is isotropic.
      float theta = 2.0 * ATMO_PI * (float(i) + 0.5) / float(ATMO_MS_SQRT);
      float cosPhi = 1.0 - 2.0 * (float(j) + 0.5) / float(ATMO_MS_SQRT);
      float sinPhi = sqrt(max(1.0 - cosPhi * cosPhi, 0.0));
      vec3 dir = vec3(sinPhi * cos(theta), cosPhi, sinPhi * sin(theta));
      AtmoScatter s = atmoIntegrate(pos, dir, sunDir, true);
      lSum += s.radiance;
      gSum += s.groundBounceAs1;
      msSum += s.multiScatterAs1;
    }
  }
  float inv = 1.0 / float(ATMO_MS_SQRT * ATMO_MS_SQRT);
  vec3 ms = msSum * inv;
  vec3 num = (term < 0.5 ? lSum : gSum) * inv;
  float ceiling = term < 0.5 ? ATMO_MS_MAX : ATMO_MS_GND_MAX;
  vec3 sum = num / max(vec3(1.0) - ms, vec3(1e-4));
  // A per-channel clip greys an absorbing haze's field: Titan's red and green
  // both reach the ceiling where its blue does not.
  float keep = min(1.0, ceiling / max(max(sum.r, sum.g), max(sum.b, 1e-12)));
  gl_FragColor = vec4(uAtmoMsKeepHue > 0.5 ? sum * keep : min(sum, vec3(ceiling)), 1.0);
}
`;

/** A dust row's sky irradiance on the ground per unit sun, cosine-weighted over the upper hemisphere a metre up. */
const GROUND_DIFFUSE_FRAG = /* glsl */ `
${ATMO_COMMON_GLSL}
#define ATMO_STEPS ${MULTI_SCATTER_STEPS}
#define ATMO_MS_APPROX 1
#define ATMO_UNIFORM_PHASE 1
${ATMO_INTEGRATE_GLSL}
void main() {
  float muSun = (gl_FragCoord.x - 0.5) / ${GROUND_DIFFUSE_W - 1}.0 * 2.0 - 1.0;
  vec3 sunDir = normalize(vec3(sqrt(max(1.0 - muSun * muSun, 0.0)), muSun, 0.0));
  vec3 pos = vec3(0.0, uAtmoRadii.x + 1.0, 0.0);
  vec3 e = vec3(0.0);
  for (int a = 0; a < ${GROUND_DIFFUSE_ROWS}; a++) {
    float cp = (float(a) + 0.5) / ${GROUND_DIFFUSE_ROWS}.0;
    float sp = sqrt(1.0 - cp * cp);
    for (int b = 0; b < ${2 * GROUND_DIFFUSE_ROWS}; b++) {
      float th = 2.0 * ATMO_PI * (float(b) + 0.5) / ${2 * GROUND_DIFFUSE_ROWS}.0;
      e += atmoIntegrate(pos, vec3(sp * cos(th), cp, sp * sin(th)), sunDir, false).radiance * cp;
    }
  }
  gl_FragColor = vec4(e * (ATMO_PI / ${GROUND_DIFFUSE_ROWS * GROUND_DIFFUSE_ROWS}.0), 1.0);
}
`;

const SKY_VIEW_FRAG = /* glsl */ `
${ATMO_COMMON_GLSL}
#define ATMO_STEPS ${SKY_VIEW_STEPS}
#define ATMO_MS_APPROX 1
#define ATMO_UNIFORM_PHASE 0
${ATMO_INTEGRATE_GLSL}

void main() {
  vec2 x = (gl_FragCoord.xy - 0.5) / (uAtmoSkyViewSize - 1.0);
  float r = clamp(uAtmoViewHeight, uAtmoRadii.x + 1.0, uAtmoRadii.y - 1.0);
  float muView, lightViewCos;
  atmoSkyViewUvToParams(x, r, muView, lightViewCos);
  float sinView = sqrt(max(1.0 - muView * muView, 0.0));
  vec3 dir = vec3(
    sinView * lightViewCos,
    muView,
    sinView * sqrt(max(1.0 - lightViewCos * lightViewCos, 0.0)));
  float muSun = clamp(uAtmoSunDir.y, -1.0, 1.0);
  vec3 sunDir = normalize(vec3(sqrt(max(1.0 - muSun * muSun, 0.0)), muSun, 0.0));
  vec3 pos = vec3(0.0, r, 0.0);
  AtmoScatter s = atmoIntegrate(pos, normalize(dir), sunDir, true);
  // The sky stays a CONSTANT-albedo consumer: this table is precomputed, and a
  // sky that tracked one fragment's ground would be the wrong sky for the next.
  gl_FragColor = vec4(s.radiance + uAtmoGroundAlbedo * s.groundBounceAs1, 1.0);
}
`;

const PROBE_SKY_N = 16;
const PROBE_SKY_FRAG = /* glsl */ `
${ATMO_COMMON_GLSL}
#define ATMO_STEPS ${SKY_VIEW_STEPS}
#define ATMO_MS_APPROX 1
#define ATMO_UNIFORM_PHASE 0
${ATMO_INTEGRATE_GLSL}
uniform vec3 uProbeDirs[${PROBE_SKY_N}];
uniform vec3 uProbeSun;
void main() {
  int k = int(gl_FragCoord.x);
  vec3 dir = vec3(0.0, 1.0, 0.0);
  for (int i = 0; i < ${PROBE_SKY_N}; i++) if (i == k) dir = uProbeDirs[i];
  float r = clamp(uAtmoViewHeight, uAtmoRadii.x + 1.0, uAtmoRadii.y - 1.0);
  AtmoScatter s = atmoIntegrate(vec3(0.0, r, 0.0), normalize(dir), normalize(uProbeSun), true);
  gl_FragColor = vec4(s.radiance + uAtmoGroundAlbedo * s.groundBounceAs1, 1.0);
}
`;

// ── the handle ──────────────────────────────────────────────────────────────

function makeTarget(width: number, height: number, name: string): THREE.WebGLRenderTarget {
  const t = new THREE.WebGLRenderTarget(width, height, {
    type: THREE.HalfFloatType,
    format: THREE.RGBAFormat,
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    wrapS: THREE.ClampToEdgeWrapping,
    wrapT: THREE.ClampToEdgeWrapping,
    depthBuffer: false,
    stencilBuffer: false,
    generateMipmaps: false,
  });
  t.texture.name = name;
  // A table of coefficients, not a picture: no transfer function on the way out.
  t.texture.colorSpace = THREE.NoColorSpace;
  return t;
}

// Scratch for sunDeckTransmittance's chord math, reused across every call
// (including across atmosphere instances) instead of a fresh closure and
// tuple per frame; safe because the function is synchronous, non-reentrant.
const deckSpanTop: [number, number] = [0, 0];
const deckSpanBase: [number, number] = [0, 0];

/** Chord endpoints of the sphere `radius` along `mu` from height `R`, into `out`. */
function deckSpan(radius: number, R: number, mu: number, out: [number, number]): boolean {
  const b = R * mu;
  const disc = b * b - (R * R - radius * radius);
  if (disc < 0) return false;
  const s = Math.sqrt(disc);
  out[0] = -b - s;
  out[1] = -b + s;
  return true;
}

export function createAtmosphere(
  config: {
    renderer: THREE.WebGLRenderer; row: BodyRow; greyGroundAlbedo?: boolean;
    /** Instruments only: the dust table's grid (tools/preview/dust-sky-check.mjs --inject coarse). */
    dustGrid?: DustGrid;
    dustStop?: 'x1' | 'bh';
  },
): AtmosphereHandle {
  const { renderer, row } = config;
  /** `descent-dojo --skyheight --inject sky-olive`: the grey ground bounce back. */
  const greyGroundAlbedo = config.greyGroundAlbedo === true;
  if (row.atmosphere.kind !== 'data') {
    throw new Error(
      `createAtmosphere: ${row.id} carries no atmosphere parameters (${row.atmosphere.kind})`);
  }
  const air: Atmosphere = row.atmosphere;

  /**
   * The opaque deck, as two altitudes and one optical depth.
   *
   * `beta_m_550_per_m * h_aerosol_m` IS the column canon publishes for the deck
   * ("opaque, tau about 30"), which is why the slab needs no depth field of its
   * own; the row's `source` says so. A row with a base and no top has a deck of
   * no thickness, which draws as a hard terminus and never as fog.
   */
  const deckBaseM = air.opaque_deck_m !== undefined && air.opaque_deck_m > 0
    ? air.opaque_deck_m : 0;
  const declaresDeck = deckBaseM > 0;
  const deckTopM = Math.max(air.deck_top_m ?? deckBaseM, deckBaseM);
  const deckExt = deckTopM > deckBaseM
    ? (air.beta_m_550_per_m * air.h_aerosol_m) / (deckTopM - deckBaseM) : 0;
  const deckAlbedoValue: readonly [number, number, number] = deckAlbedo(air);
  const groundAlbedoValue: readonly [number, number, number] = groundAlbedoRgb(air);
  const deckFloorFlux: readonly [number, number, number] = air.deck_floor_flux ?? [0, 0, 0];

  const targets: Record<AtmosphereLutName, THREE.WebGLRenderTarget> = {
    transmittance: makeTarget(TRANSMITTANCE_W, TRANSMITTANCE_H, `${row.id}-transmittance`),
    multipleScattering: makeTarget(
      MULTI_SCATTER_W * MULTI_SCATTER_TERMS, MULTI_SCATTER_H, `${row.id}-multiscatter`),
    skyView: makeTarget(SKY_VIEW_W, SKY_VIEW_H, `${row.id}-skyview`),
  };
  let skyViewPrev = makeTarget(SKY_VIEW_W, SKY_VIEW_H, `${row.id}-skyview-prev`);

  /** `--veil --inject veil` only: see `atmoSunVisibility`. */
  let hardSunShadow = false;
  const oldHaze = oldAir() && columnTau550(air) > 1;

  const uniforms: Record<string, THREE.IUniform> = {
    uAtmoBetaR: { value: new THREE.Vector3(...air.beta_r_per_m) },
    uAtmoBetaM: { value: air.beta_m_550_per_m },
    // The control greys the haze only past unit depth, so Mars keeps its dust's albedo.
    uAtmoMieExt: { value: new THREE.Vector3(...(oldHaze ? [1, 1, 1] : air.aerosol_ext_rgb ?? [1, 1, 1])) },
    uAtmoMieSsa: { value: new THREE.Vector3(...(oldHaze ? [1, 1, 1] : air.aerosol_ssa ?? [1, 1, 1])) },
    // Only a haze both thick and absorbing: every other row keeps the clip its tables were checked with.
    uAtmoMsKeepHue: { value: !oldHaze && columnTau550(air) > 1 && (air.aerosol_ssa ?? [1]).some((v) => v < 1) ? 1 : 0 },
    uAtmoScaleH: { value: new THREE.Vector2(air.h_rayleigh_m, air.h_aerosol_m) },
    uAtmoRadii: { value: new THREE.Vector2(row.radius_m, row.radius_m) },
    uAtmoMieG: { value: air.mie_g },
    uAtmoMieLobes: { value: air.mie_lobes ? 1 : 0 },
    uAtmoMieG1: { value: new THREE.Vector3(...(air.mie_lobes?.g1_rgb ?? [air.mie_g, air.mie_g, air.mie_g])) },
    uAtmoMieAlpha: { value: new THREE.Vector3(...(air.mie_lobes?.alpha_rgb ?? [1, 1, 1])) },
    uAtmoMieG2: { value: air.mie_lobes?.g2 ?? 0 },
    uAtmoGroundAlbedo: { value: new THREE.Vector3(air.ground_albedo, air.ground_albedo,
      air.ground_albedo) },
    uAtmoSunDir: { value: new THREE.Vector3(0, 1, 0) },
    uAtmoViewHeight: { value: row.radius_m },
    uAtmoEyeHeight: { value: row.radius_m },
    uAtmoTransmittance: { value: targets.transmittance.texture },
    uAtmoMultiScatter: { value: targets.multipleScattering.texture },
    uAtmoSkyView: { value: targets.skyView.texture },
    uAtmoSkyViewPrev: { value: skyViewPrev.texture },
    uAtmoSkyViewExtrap: { value: 0 },
    uAtmoTransmittanceSize: { value: new THREE.Vector2(TRANSMITTANCE_W, TRANSMITTANCE_H) },
    uAtmoMultiScatterSize: { value: new THREE.Vector2(MULTI_SCATTER_W, MULTI_SCATTER_H) },
    uAtmoSkyViewSize: { value: new THREE.Vector2(SKY_VIEW_W, SKY_VIEW_H) },
    uAtmoDeckRadii: { value: new THREE.Vector2(0, 0) },
    uAtmoDeckExt: { value: 0 },
    uAtmoDeckRadiance: { value: new THREE.Vector3(0, 0, 0) },
    uAtmoHardSun: { value: 0 },
    uAtmoMsHoldGround: { value: 0 },
    uAtmoWhite: { value: new THREE.Vector3(1, 1, 1) },
  };
  const lobes = air.mie_lobes ?? null;
  const dustGrid = config.dustGrid ?? DUST_GRID;
  // The dust table's code is compiled only into a row that has one: carried as a branch it slowed Titan's aerial march 20%.
  const glslHead = lobes ? `#define ATMO_BEAM 1\n${dustGlslDefines(dustGrid)}` : '';
  const dust = lobes ? createDustTables({
    renderer,
    uniforms,
    common: glslHead + ATMO_COMMON_GLSL,
    vertexShader: FULLSCREEN_VERT,
    grid: dustGrid,
    name: `${row.id}-dust-q`,
    ...(config.dustStop ? { stop: config.dustStop } : {}),
    onTexture: (t) => {
      uniforms['uAtmoDustQ']!.value = t;
      due.skyView = true;
      dustBuilds++;
    },
  }) : null;
  // Zeros until the first build lands: single scattering off the direct sun and the MS term alone.
  if (dust) uniforms['uAtmoDustQ'] = { value: dust.texture };

  const passMaterial = (frag: string, name: string): THREE.ShaderMaterial => {
    const m = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: FULLSCREEN_VERT,
      fragmentShader: glslHead + frag,
      depthTest: false,
      depthWrite: false,
    });
    m.name = name;
    // Every table is linear data. Tone mapping it once at write and again at
    // read is the silent version of this whole module going wrong.
    m.toneMapped = false;
    return m;
  };
  const materials: Record<AtmosphereLutName, THREE.ShaderMaterial> = {
    transmittance: passMaterial(TRANSMITTANCE_FRAG, 'atmo-transmittance'),
    multipleScattering: passMaterial(MULTI_SCATTER_FRAG, 'atmo-multiscatter'),
    skyView: passMaterial(SKY_VIEW_FRAG, 'atmo-skyview'),
  };
  const groundDiffuse = lobes ? (() => {
    const blank = new THREE.DataTexture(new Uint16Array(4), 1, 1, THREE.RGBAFormat, THREE.HalfFloatType);
    blank.needsUpdate = true;
    uniforms['uAtmoGroundDiffuse'] = { value: blank };
    uniforms['uAtmoGroundDiffuseOn'] = { value: 0 };
    return {
      blank,
      target: makeTarget(GROUND_DIFFUSE_W, 1, `${row.id}-ground-diffuse`),
      material: passMaterial(GROUND_DIFFUSE_FRAG, 'atmo-ground-diffuse'),
    };
  })() : null;

  const quadGeometry = new THREE.PlaneGeometry(2, 2);
  const quad = new THREE.Mesh(quadGeometry, materials.transmittance);
  quad.frustumCulled = false;
  const lutScene = new THREE.Scene();
  lutScene.add(quad);
  const lutCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  const savedViewport = new THREE.Vector4();
  const savedScissor = new THREE.Vector4();
  const savedClearColor = new THREE.Color();

  const due: Record<AtmosphereLutName, boolean> = {
    transmittance: true, multipleScattering: true, skyView: true,
  };
  const byPass: Record<AtmosphereLutName, number> = {
    transmittance: 0, multipleScattering: 0, skyView: 0,
  };
  const msByPass: Record<AtmosphereLutName, number> = {
    transmittance: 0, multipleScattering: 0, skyView: 0,
  };
  let passesRun = 0;
  let worstPassMs = 0;
  let worstPass: AtmosphereLutName | null = null;
  let lastPassMs = 0;
  const recentPasses: AtmospherePass[] = [];
  let syncPasses = false;
  let injectPassMs = 0;
  let canvasViewport = false;
  let nonFiniteFrame: 'altitude' | 'sun' | 'both' | null = null;
  const nanSun = new THREE.Vector3(Number.NaN, Number.NaN, 0);
  let nonFiniteGuardOff = false;
  let nonFiniteRefused = 0;
  let yieldedToTiles = 0;
  let yieldedInARow = 0;
  let disposed = false;

  let scaleHeightScale = 1;
  let betaMScale = 1;
  /** True while the eye is under the deck's TOP, which is where the LUT stops
   *  being the air over the deck and becomes the sub-cloud shell. */
  let underDeck = false;
  /** True while the eye is under the deck's BASE, which is the face of the slab
   *  it sees: the cloud top from over it, the overcast ceiling from under. */
  let faceUnder = false;
  /** `descent-dojo --deck --inject nodeck`: the deck unread, which is the shell
   *  run from the datum that clipped Venus's disc to white. */
  let deckOff = false;
  /** `descent-dojo --sky --inject deckflip`: the deck's face read blue over
   *  red, which is the colour the row does NOT predict. */
  let deckFlip = false;
  const hasDeck = (): boolean => declaresDeck && !deckOff;
  let tau: number | null = null;
  let viewHeightM = row.radius_m;
  let eyeHeightM = row.radius_m;
  let sunMu = 1;
  const lutSunDir = new THREE.Vector3(0, 1, 0);
  let lutAltitudeM = -1;
  /** The sun heights (`sunDir.y`) the sky-view table and the one before it were
   *  built for; null where a table cannot be paired (unbuilt, or other air). */
  let skyViewMu: number | null = null;
  let skyViewPrevMu: number | null = null;
  /** `descent-dojo --sunset-sweep --inject sky-steps`: the newest table drawn alone. */
  let skySteps = false;
  /** `--sunset-sweep`'s streaming leg: every frame taken as one the tile mounter built on. */
  let tilesEveryFrame = false;

  /**
   * Rebuild the derived parameters and hand them to the uniforms.
   *
   * Mars's aerosol coefficient is a function of the season, not a constant: the
   * registry's `beta_m_550_per_m` times `h_aerosol_m` IS a column optical depth
   * (0.50 for Mars), so a seasonal tau replaces it outright as `tau / H`.
   * `dustOpticalDepth(Ls)` in src/atlas/plotField.ts is where Mars's comes from.
   */
  function applyParams(): void {
    const hR = air.h_rayleigh_m * scaleHeightScale;
    const hM = air.h_aerosol_m * scaleHeightScale;
    // An opaque deck terminates the LUT, and which side of it the eye is on is
    // what the tables are for. Above the deck the shell starts at the deck TOP
    // with whatever the registry's own exponentials leave up there - running
    // the surface exponential from the datum is what put a column in the tens
    // over Venus and clipped the disc to white. Under it the shell is the
    // sub-cloud air with its ceiling at the deck BASE and no aerosol at all,
    // because that aerosol is the deck, 48 km overhead.
    const groundAltM = hasDeck() && !underDeck ? deckTopM : 0;
    const rGround = row.radius_m + groundAltM;
    const topM = hasDeck() && underDeck
      ? deckBaseM
      : ATMOSPHERE_TOP_SCALE_HEIGHTS * Math.max(hR, hM);
    const rebaseR = Math.exp(-groundAltM / Math.max(hR, 1));
    // Against the REGISTRY's scale height, not the debugged one. `tau` is a
    // physical column and beta_m follows from it, so a knob that moved both
    // would conserve the column it exists to falsify - which is what
    // `atmo-check --gpu --self` read as a ratio of 1.00 the first time.
    const betaM0 = (tau === null ? air.beta_m_550_per_m : tau / Math.max(air.h_aerosol_m, 1))
      * betaMScale;
    const betaM = hasDeck()
      ? (underDeck ? 0 : betaM0 * Math.exp(-groundAltM / Math.max(hM, 1)))
      : betaM0;
    (uniforms['uAtmoBetaR']!.value as THREE.Vector3).set(
      air.beta_r_per_m[0] * rebaseR, air.beta_r_per_m[1] * rebaseR, air.beta_r_per_m[2] * rebaseR);
    (uniforms['uAtmoScaleH']!.value as THREE.Vector2).set(hR, hM);
    (uniforms['uAtmoRadii']!.value as THREE.Vector2).set(rGround, rGround + topM);
    uniforms['uAtmoBetaM']!.value = betaM;
    const rowStepM = topM / (MULTI_SCATTER_H - 1);
    const rowStepTau = air.beta_r_per_m[1] * rebaseR * hR * (1 - Math.exp(-rowStepM / Math.max(hR, 1)))
      + betaM * hM * (1 - Math.exp(-rowStepM / Math.max(hM, 1)));
    uniforms['uAtmoMsHoldGround']!.value = !hasDeck() && !oldAir() && rowStepTau > 1 ? 1 : 0;
    // Over the deck the LUT's ground IS the cloud top, so the albedo every
    // constant-albedo consumer reads is the cloud top's and not the rock's.
    // Both are triples: what bounces off the ground and back into the air
    // carries the ground's own colour, and averaging that away is what left
    // Mars's sky the grey a conservative scatterer gives.
    const bounce = greyGroundAlbedo
      ? [air.ground_albedo, air.ground_albedo, air.ground_albedo] as const
      : (hasDeck() && !underDeck ? deckAlbedoValue : groundAlbedoValue);
    (uniforms['uAtmoGroundAlbedo']!.value as THREE.Vector3).set(
      bounce[0], bounce[1], bounce[2]);
    (uniforms['uAtmoDeckRadii']!.value as THREE.Vector2).set(
      hasDeck() ? row.radius_m + deckBaseM : 0, hasDeck() ? row.radius_m + deckTopM : 0);
    uniforms['uAtmoDeckExt']!.value = hasDeck() ? deckExt : 0;
    // Reflectance from outside the slab, transmitted flux from under it. The
    // eye's own radius picks, once a frame, so the fragment does not have to.
    const face = faceUnder ? deckFloorFlux : deckAlbedoValue;
    const r = deckFlip ? face[2] : face[0];
    const b = deckFlip ? face[0] : face[2];
    (uniforms['uAtmoDeckRadiance']!.value as THREE.Vector3).set(
      r / Math.PI, face[1] / Math.PI, b / Math.PI);
  }
  applyParams();

  /**
   * What the opaque deck leaves of a ray from the eye along mu: `atmoDeck`'s
   * near-wall chord, with no far limit because the sun is behind everything.
   */
  function sunDeckTransmittance(mu: number): number {
    if (!hasDeck() || deckExt <= 0) return 1;
    const R = eyeHeightM;
    if (!deckSpan(row.radius_m + deckTopM, R, mu, deckSpanTop) || deckSpanTop[1] <= 0) return 1;
    let t0 = Math.max(deckSpanTop[0], 0);
    let t1 = Math.max(deckSpanTop[1], 0);
    if (deckSpan(row.radius_m + deckBaseM, R, mu, deckSpanBase) && deckSpanBase[1] > 0) {
      if (deckSpanBase[0] > 0) t1 = Math.min(t1, deckSpanBase[0]);
      else t0 = Math.max(t0, deckSpanBase[1]);
    }
    return t1 > t0 ? Math.exp(-deckExt * (t1 - t0)) : 1;
  }

  function invalidateAll(): void {
    due.transmittance = true;
    due.multipleScattering = true;
    due.skyView = true;
    skyViewMu = null;
    skyViewPrevMu = null;
  }

  /** Keep the sky-view table about to be replaced as the line's other end, when the two pair. */
  function pairSkyView(): void {
    const mu = (uniforms['uAtmoSunDir']!.value as THREE.Vector3).y;
    const a = Math.max(viewHeightM - (uniforms['uAtmoRadii']!.value as THREE.Vector2).x, SKY_VIEW_ALTITUDE_FLOOR_M);
    const b = Math.max(lutAltitudeM, SKY_VIEW_ALTITUDE_FLOOR_M);
    if (skyViewMu === null || (a > b ? a / b : b / a) > SKY_VIEW_PAIR_ALTITUDE_RATIO) {
      skyViewPrevMu = null;
      return;
    }
    // A sun that moved only in azimuth rebuilds the same table in place.
    if (Math.abs(mu - skyViewMu) < 1e-6) return;
    const t = targets.skyView;
    targets.skyView = skyViewPrev;
    skyViewPrev = t;
    uniforms['uAtmoSkyView']!.value = targets.skyView.texture;
    uniforms['uAtmoSkyViewPrev']!.value = skyViewPrev.texture;
    skyViewPrevMu = skyViewMu;
  }

  /** The newest sky-view table's sun height less the one before's, or null where they do not pair. */
  function skyViewSpan(): number | null {
    if (skySteps || skyViewMu === null || skyViewPrevMu === null) return null;
    const span = skyViewMu - skyViewPrevMu;
    return Math.abs(span) < 1e-6 || Math.abs(span) > SKY_VIEW_PAIR_MAX_MU ? null : span;
  }

  function skyViewExtrap(): number {
    const span = skyViewSpan();
    if (span === null || skyViewMu === null) return 0;
    return THREE.MathUtils.clamp((sunMu - skyViewMu) / span, -SKY_VIEW_EXTRAP_MAX, SKY_VIEW_EXTRAP_MAX);
  }

  /**
   * The change in sun height that owes a sky-view pass. With no pair a short
   * one, then twice the pair's spacing up to `SKY_VIEW_PAIR_MOVE_DEG`, so a sun
   * set moving by a scrub is followed by a line within a few frames.
   */
  function skyViewMoveDeg(): number {
    if (skySteps) return SKY_VIEW_SUN_MOVE_DEG;
    const span = skyViewSpan();
    if (span === null || skyViewPrevMu === null || skyViewMu === null) return SKY_VIEW_FIRST_MOVE_DEG;
    const spanDeg = Math.abs(sunHeightDeg(skyViewMu) - sunHeightDeg(skyViewPrevMu));
    return THREE.MathUtils.clamp(2 * spanDeg, SKY_VIEW_FIRST_MOVE_DEG, SKY_VIEW_PAIR_MOVE_DEG);
  }

  function runPass(name: AtmosphereLutName): void {
    if (name === 'skyView') pairSkyView();
    const target = targets[name];
    const gl = syncPasses ? renderer.getContext() : null;
    // Drain what the frame has already queued, so the interval below is this
    // pass's own GPU time and not the backlog it happened to be submitted after.
    gl?.finish();
    const t0 = performance.now();
    // The Atlas draws inside stage.begin(), which owns the viewport, the
    // scissor rect and the scissor test; a pass that does not give all four
    // back leaves the stage describing a rect that has nothing to do with what
    // is being drawn (src/world/stage.ts).
    const prevTarget = renderer.getRenderTarget();
    const prevCubeFace = renderer.getActiveCubeFace();
    const prevMipLevel = renderer.getActiveMipmapLevel();
    renderer.getViewport(savedViewport);
    renderer.getScissor(savedScissor);
    const prevScissorTest = renderer.getScissorTest();

    const draw = (to: THREE.WebGLRenderTarget, material: THREE.ShaderMaterial): void => {
      quad.material = material;
      // The bound target's own viewport covers it in texels. `setViewport` takes canvas
      // pixels and scales them by the pixel ratio, which under 1 leaves the far texels unwritten.
      renderer.setRenderTarget(to);
      if (canvasViewport) {
        renderer.setViewport(0, 0, to.width, to.height);
        renderer.setScissor(0, 0, to.width, to.height);
      }
      renderer.setScissorTest(false);
      renderer.render(lutScene, lutCamera);
    };
    if (name === 'multipleScattering' && groundDiffuse) {
      // Two MS passes with the sky's irradiance on the ground between them, so the bounce carries the sky as well.
      uniforms['uAtmoGroundDiffuseOn']!.value = 0;
      uniforms['uAtmoGroundDiffuse']!.value = groundDiffuse.blank;
      draw(target, materials.multipleScattering);
      draw(groundDiffuse.target, groundDiffuse.material);
      uniforms['uAtmoGroundDiffuse']!.value = groundDiffuse.target.texture;
      uniforms['uAtmoGroundDiffuseOn']!.value = 1;
    }
    draw(target, materials[name]);

    renderer.setRenderTarget(prevTarget, prevCubeFace, prevMipLevel);
    renderer.setViewport(savedViewport);
    renderer.setScissor(savedScissor);
    renderer.setScissorTest(prevScissorTest);

    if (injectPassMs > 0) {
      const until = performance.now() + injectPassMs;
      while (performance.now() < until) { /* the known cost the self arm injects */ }
    }
    gl?.finish();

    if (name === 'skyView') {
      lutSunDir.copy(uniforms['uAtmoSunDir']!.value as THREE.Vector3);
      lutAltitudeM = viewHeightM - (uniforms['uAtmoRadii']!.value as THREE.Vector2).x;
      skyViewMu = lutSunDir.y;
    }
    due[name] = false;
    // The dust table reads the transmittance and MS tables, so it follows them; the sun moving does not owe it.
    if (name === 'multipleScattering') dust?.invalidate();
    yieldedInARow = 0;
    const ms = performance.now() - t0;
    const first = byPass[name] === 0;
    passesRun++;
    byPass[name]++;
    msByPass[name] = Math.max(msByPass[name], ms);
    lastPassMs = ms;
    if (ms > worstPassMs) { worstPassMs = ms; worstPass = name; }
    recentPasses.push({ seq: passesRun, name, ms, gpuSynced: syncPasses, first, injectedMs: injectPassMs });
    if (recentPasses.length > PASS_LOG) recentPasses.shift();
  }

  function clearTables(): void {
    const prevTarget = renderer.getRenderTarget();
    renderer.getClearColor(savedClearColor);
    const prevClearAlpha = renderer.getClearAlpha();
    renderer.setClearColor(0x000000, 0);
    for (const name of LUT_NAMES) {
      renderer.setRenderTarget(targets[name]);
      renderer.clear(true, false, false);
    }
    renderer.setRenderTarget(prevTarget);
    renderer.setClearColor(savedClearColor, prevClearAlpha);
  }

  /** One draw of the dust table's build, with the renderer's target, viewport and scissor given back. */
  function runDust(): void {
    if (!dust) return;
    const prevTarget = renderer.getRenderTarget();
    const prevCubeFace = renderer.getActiveCubeFace();
    const prevMipLevel = renderer.getActiveMipmapLevel();
    renderer.getViewport(savedViewport);
    renderer.getScissor(savedScissor);
    const prevScissorTest = renderer.getScissorTest();
    const t0 = performance.now();
    dust.step(canvasViewport);
    dustSubmitMs += performance.now() - t0;
    renderer.setRenderTarget(prevTarget, prevCubeFace, prevMipLevel);
    renderer.setViewport(savedViewport);
    renderer.setScissor(savedScissor);
    renderer.setScissorTest(prevScissorTest);
    yieldedInARow = 0;
  }
  let dustSubmitMs = 0;
  let dustBuilds = 0;

  /** Which pass is owed next. Transmittance first: the other two sample it. The dust table after a first sky. */
  function nextDue(): AtmosphereLutName | 'dust' | null {
    for (const name of LUT_NAMES) if (due[name]) return name;
    if (dust && dust.pending() > 0) return 'dust';
    return null;
  }
  const runNext = (name: AtmosphereLutName | 'dust'): void => (name === 'dust' ? runDust() : runPass(name));

  const handle: AtmosphereHandle = {
    uniforms,
    glsl: glslHead + ATMO_SAMPLING_CHUNK,
    glslKey: lobes ? `+dust${dustGrid.nr}x${dustGrid.na}x${dustGrid.nb}x${dustGrid.nphi}x${dustGrid.slices}` : '',

    update(sunDir, cameraAltitudeM, nextTau = null, mayRunPass = true): void {
      if (disposed) return;
      if (nonFiniteFrame) {
        if (nonFiniteFrame !== 'sun') cameraAltitudeM = Number.NaN;
        if (nonFiniteFrame !== 'altitude') sunDir = nanSun;
        nonFiniteFrame = null;
        due.skyView = true;
        mayRunPass = true;
      }
      // A NaN cached below as the sky-view table's sun or height fails every
      // rebuild test from then on, so that frame keeps the last good inputs.
      if (!nonFiniteGuardOff && (!Number.isFinite(cameraAltitudeM) || !Number.isFinite(sunDir.x)
        || !Number.isFinite(sunDir.y) || !Number.isFinite(sunDir.z) || sunDir.lengthSq() === 0)) {
        nonFiniteRefused++;
        return;
      }
      if (nextTau !== null && Number.isFinite(nextTau)) {
        if (tau === null || Math.abs(nextTau - tau) > TAU_RECOMPUTE_FRACTION * Math.max(tau, 1e-6)) {
          tau = nextTau;
          applyParams();
          invalidateAll();
        }
      }
      // `cameraAltitudeM` is above the BODY's datum. The LUT's own ground is
      // the deck top while the eye is over it, so every height below is taken
      // against `radii.x` and not against the datum.
      const nextUnder = hasDeck() && cameraAltitudeM < deckTopM;
      const nextFace = hasDeck() && cameraAltitudeM < deckBaseM;
      if (nextUnder !== underDeck || nextFace !== faceUnder) {
        underDeck = nextUnder;
        faceUnder = nextFace;
        applyParams();
        invalidateAll();
      }
      const radii = uniforms['uAtmoRadii']!.value as THREE.Vector2;
      const groundAltM = radii.x - row.radius_m;
      const topM = radii.y - radii.x;
      const overGroundM = Math.max(cameraAltitudeM - groundAltM, 0);
      const altM = Math.min(overGroundM, topM);
      viewHeightM = radii.x + altM;
      uniforms['uAtmoViewHeight']!.value = viewHeightM;
      eyeHeightM = radii.x + overGroundM;
      uniforms['uAtmoEyeHeight']!.value = eyeHeightM;
      const sun = uniforms['uAtmoSunDir']!.value as THREE.Vector3;
      sun.copy(sunDir).normalize();
      sunMu = sun.y;

      if (!due.skyView) {
        // The table reads the sun's height alone, so a sun moving in azimuth owes it nothing.
        const moved = Math.abs(sunHeightDeg(sun.y) - sunHeightDeg(lutSunDir.y));
        const a = Math.max(altM, SKY_VIEW_ALTITUDE_FLOOR_M);
        const b = Math.max(lutAltitudeM, SKY_VIEW_ALTITUDE_FLOOR_M);
        const ratio = a > b ? a / b : b / a;
        if (moved > skyViewMoveDeg() || ratio > SKY_VIEW_ALTITUDE_RATIO) due.skyView = true;
      }

      dust?.trim(performance.now());
      const name = nextDue();
      // Absolute, not in the pair's spacing: yielding stretches that spacing, which would move its own bound.
      const stale = name === 'skyView' && !skySteps && skyViewMu !== null
        && Math.abs(sunHeightDeg(sunMu) - sunHeightDeg(skyViewMu)) >= SKY_VIEW_YIELD_MAX_EXTRAP * skyViewMoveDeg();
      if (name === null) {
        yieldedInARow = 0;
      } else if ((!mayRunPass || tilesEveryFrame) && !stale && yieldedInARow < MAX_YIELDED_FRAMES) {
        yieldedToTiles++;
        yieldedInARow++;
      } else {
        runNext(name);
        // The dust build reads neither the sky-view table nor the sun, so a moving sun does not hold it back.
        if (name === 'skyView' && dust && dust.pending() > 0) runDust();
      }
      uniforms['uAtmoSkyViewExtrap']!.value = skyViewExtrap();
    },

    pending(): number {
      let n = 0;
      for (const name of LUT_NAMES) if (due[name]) n++;
      return n + (dust ? dust.pending() : 0);
    },

    sunTransmittance(mu: number, out: THREE.Vector3): THREE.Vector3 {
      const radii = uniforms['uAtmoRadii']!.value as THREE.Vector2;
      const scaleH = uniforms['uAtmoScaleH']!.value as THREE.Vector2;
      const betaR = uniforms['uAtmoBetaR']!.value as THREE.Vector3;
      const betaM = uniforms['uAtmoBetaM']!.value as number;
      const mieExt = uniforms['uAtmoMieExt']!.value as THREE.Vector3;
      const rg = radii.x;
      const rt = radii.y;
      // TRANSMITTANCE_FRAG's trapezoid at the radius atmoViewTransmittance reads the table at.
      const r = THREE.MathUtils.clamp(viewHeightM, rg + 1, rt - 1);
      const m = THREE.MathUtils.clamp(mu, -1, 1);
      const d = Math.max(-r * m + Math.sqrt(Math.max(r * r * (m * m - 1) + rt * rt, 0)), 0);
      const dx = d / TRANSMITTANCE_STEPS;
      let tauR = 0;
      let tauG = 0;
      let tauB = 0;
      for (let i = 0; i <= TRANSMITTANCE_STEPS; i++) {
        const t = i * dx;
        const h = Math.sqrt(Math.max(t * t + 2 * r * m * t + r * r, rg * rg)) - rg;
        const w = i === 0 || i === TRANSMITTANCE_STEPS ? 0.5 : 1;
        const rayleigh = Math.exp(-h / scaleH.x) * w;
        const aerosol = betaM * Math.exp(-h / scaleH.y) * w;
        tauR += betaR.x * rayleigh + aerosol * mieExt.x;
        tauG += betaR.y * rayleigh + aerosol * mieExt.y;
        tauB += betaR.z * rayleigh + aerosol * mieExt.z;
      }
      const deck = sunDeckTransmittance(m);
      return out.set(Math.exp(-tauR * dx) * deck, Math.exp(-tauG * dx) * deck, Math.exp(-tauB * dx) * deck);
    },

    stats(): AtmosphereStats {
      return {
        passesRun, worstPassMs, worstPass, lastPassMs,
        byPass: { ...byPass },
        msByPass: { ...msByPass },
        pending: handle.pending(),
        ready: byPass.transmittance > 0 && byPass.multipleScattering > 0 && byPass.skyView > 0
          && (!dust || dust.pending() === 0),
        dustSubmitMs,
        dustBuilds,
        yieldedToTiles,
        yieldedInARow,
        recentPasses: recentPasses.map((r) => ({ ...r })),
        gpuSynced: syncPasses,
        injectPassMs,
        canvasViewport,
        nonFiniteRefused,
        skyViewExtrap: uniforms['uAtmoSkyViewExtrap']!.value as number,
        skySteps,
        tilesEveryFrame,
      };
    },

    params(): AtmosphereParams {
      const radii = uniforms['uAtmoRadii']!.value as THREE.Vector2;
      const scaleH = uniforms['uAtmoScaleH']!.value as THREE.Vector2;
      const beta = uniforms['uAtmoBetaR']!.value as THREE.Vector3;
      const ssa = uniforms['uAtmoMieSsa']!.value as THREE.Vector3;
      const mieExt = uniforms['uAtmoMieExt']!.value as THREE.Vector3;
      const albedo = uniforms['uAtmoGroundAlbedo']!.value as THREE.Vector3;
      return {
        bodyId: row.id,
        rGroundM: radii.x,
        rTopM: radii.y,
        hRayleighM: scaleH.x,
        hAerosolM: scaleH.y,
        betaR: [beta.x, beta.y, beta.z],
        betaM: uniforms['uAtmoBetaM']!.value as number,
        mieSsa: [ssa.x, ssa.y, ssa.z],
        mieExt: [mieExt.x, mieExt.y, mieExt.z],
        mieG: uniforms['uAtmoMieG']!.value as number,
        mieLobes: air.mie_lobes ?? null,
        groundAlbedo: (albedo.x + albedo.y + albedo.z) / 3,
        groundAlbedoRgb: [albedo.x, albedo.y, albedo.z],
        tau,
        viewHeightM,
        eyeHeightM,
        bodyRadiusM: row.radius_m,
        deckM: hasDeck() ? [deckBaseM, deckTopM] : null,
        underDeck,
        deckFlip,
        sunMu,
        scaleHeightScale,
        betaMScale,
        hardSunShadow,
      };
    },

    flush(): void {
      if (disposed) return;
      for (;;) {
        const name = nextDue();
        if (name === null) return;
        runNext(name);
      }
    },

    readLut(name): AtmosphereLutRead | { ok: false; why: string } {
      if (disposed) return { ok: false, why: 'the atmosphere is disposed' };
      if (!LUT_NAMES.includes(name as AtmosphereLutName)) {
        return { ok: false, why: `no LUT called "${name}" (${LUT_NAMES.join(', ')})` };
      }
      const lut = name as AtmosphereLutName;
      const target = targets[lut];
      const buf = new Uint16Array(target.width * target.height * 4);
      renderer.readRenderTargetPixels(target, 0, 0, target.width, target.height, buf);
      const data = new Array<number>(buf.length);
      for (let i = 0; i < buf.length; i++) data[i] = THREE.DataUtils.fromHalfFloat(buf[i]!);
      // readPixels hands back the bottom row first, and the passes write x.y
      // from gl_FragCoord.y, so row 0 IS the table's v = 0 edge.
      return { ok: true, name: lut, width: target.width, height: target.height, channels: 4, data };
    },

    setDebug(next): void {
      let moved = false;
      if (next.scaleHeightScale !== undefined) {
        const k = next.scaleHeightScale === null ? 1 : next.scaleHeightScale;
        if (k > 0 && k !== scaleHeightScale) {
          scaleHeightScale = k;
          applyParams();
          moved = true;
        }
      }
      if (next.betaMScale !== undefined) {
        const k = next.betaMScale === null ? 1 : next.betaMScale;
        if (k > 0 && k !== betaMScale) {
          betaMScale = k;
          applyParams();
          moved = true;
        }
      }
      if (next.deckOff !== undefined && next.deckOff !== deckOff) {
        deckOff = next.deckOff;
        underDeck = false;
        faceUnder = false;
        applyParams();
        moved = true;
      }
      if (next.deckFlip !== undefined && next.deckFlip !== deckFlip) {
        deckFlip = next.deckFlip;
        applyParams();
        moved = true;
      }
      if (next.syncPasses !== undefined && next.syncPasses !== syncPasses) {
        syncPasses = next.syncPasses;
        moved = true;
      }
      if (next.injectPassMs !== undefined) {
        const ms = next.injectPassMs === null ? 0 : Math.max(0, next.injectPassMs);
        if (ms !== injectPassMs) { injectPassMs = ms; moved = true; }
      }
      if (next.canvasViewport !== undefined && next.canvasViewport !== canvasViewport) {
        canvasViewport = next.canvasViewport;
        moved = true;
      }
      if (next.clearTables) {
        clearTables();
        moved = true;
      }
      if (next.nonFiniteFrame !== undefined) nonFiniteFrame = next.nonFiniteFrame;
      if (next.nonFiniteGuardOff !== undefined) nonFiniteGuardOff = next.nonFiniteGuardOff;
      // Neither moves a table: one is how the tables are read, the other when they are built.
      if (next.skySteps !== undefined) skySteps = next.skySteps;
      if (next.tilesEveryFrame !== undefined) tilesEveryFrame = next.tilesEveryFrame;
      // No table reads it - the aerial march is per fragment - so this one is
      // deliberately not in `moved`: invalidating three LUTs to move a
      // fragment-time branch would cost the arm a precompute window it is not
      // measuring.
      if (next.hardSunShadow !== undefined && next.hardSunShadow !== hardSunShadow) {
        hardSunShadow = next.hardSunShadow;
        uniforms['uAtmoHardSun']!.value = hardSunShadow ? 1 : 0;
      }
      // Every knob invalidates: a measurement mode armed after the tables are
      // current would have nothing to measure.
      if (moved) invalidateAll();
    },

    probeGlsl(): { ok: boolean; why?: string; texel?: number[] } {
      if (disposed) return { ok: false, why: 'the atmosphere is disposed' };
      const material = new THREE.ShaderMaterial({
        uniforms,
        vertexShader: FULLSCREEN_VERT,
        fragmentShader: glslHead + GLSL_PROBE_FRAG,
        depthTest: false,
        depthWrite: false,
      });
      material.toneMapped = false;
      const target = makeTarget(4, 4, `${row.id}-glsl-probe`);
      const mesh = new THREE.Mesh(quadGeometry, material);
      const probeScene = new THREE.Scene();
      probeScene.add(mesh);
      try {
        // A shader that will not compile reaches the instrument as a console
        // error out of three's own diagnostics, which the arm turns into a
        // fault; the readback is what says the chunk also EVALUATES.
        const prevTarget = renderer.getRenderTarget();
        renderer.setRenderTarget(target);
        renderer.render(probeScene, lutCamera);
        const buf = new Uint16Array(4);
        renderer.readRenderTargetPixels(target, 0, 0, 1, 1, buf);
        renderer.setRenderTarget(prevTarget);
        const texel = Array.from(buf, (h) => THREE.DataUtils.fromHalfFloat(h));
        const finite = texel.every((v) => Number.isFinite(v));
        // Finite is not enough: a shader that fails to compile draws nothing
        // and the target keeps the clear colour, whose channels are finite. The
        // probe's own fragment adds the transmittance over a 2 km ray, which is
        // near 1 per channel at any altitude, so a sum this low is a fragment
        // that never ran.
        const sum = texel.slice(0, 3).reduce((a, v) => a + v, 0);
        return finite && sum > GLSL_PROBE_MIN_SUM
          ? { ok: true, texel }
          : { ok: false, why: `probe texel ${JSON.stringify(texel)} sums to ${sum}`, texel };
      } finally {
        probeScene.remove(mesh);
        material.dispose();
        target.dispose();
      }
    },

    probeAerial(dirLocal, sunLocal, distanceM): AtmosphereAerialProbe | { ok: false; why: string } {
      if (disposed) return { ok: false, why: 'the atmosphere is disposed' };
      const probeUniforms: Record<string, THREE.IUniform> = {
        ...uniforms,
        uProbeDir: { value: new THREE.Vector3(...dirLocal) },
        uProbeSun: { value: new THREE.Vector3(...sunLocal) },
        uProbeDist: { value: distanceM },
      };
      const material = new THREE.ShaderMaterial({
        uniforms: probeUniforms,
        vertexShader: FULLSCREEN_VERT,
        fragmentShader: glslHead + PROBE_AERIAL_FRAG,
        depthTest: false,
        depthWrite: false,
      });
      material.toneMapped = false;
      const target = makeTarget(3, 1, `${row.id}-aerial-probe`);
      const mesh = new THREE.Mesh(quadGeometry, material);
      const probeScene = new THREE.Scene();
      probeScene.add(mesh);
      try {
        const prevTarget = renderer.getRenderTarget();
        renderer.setRenderTarget(target);
        renderer.render(probeScene, lutCamera);
        const buf = new Uint16Array(3 * 4);
        renderer.readRenderTargetPixels(target, 0, 0, 3, 1, buf);
        renderer.setRenderTarget(prevTarget);
        const texel = (i: number): readonly [number, number, number] => [
          THREE.DataUtils.fromHalfFloat(buf[i * 4]!),
          THREE.DataUtils.fromHalfFloat(buf[i * 4 + 1]!),
          THREE.DataUtils.fromHalfFloat(buf[i * 4 + 2]!),
        ];
        return { ok: true, inScatter: texel(0), transmittance: texel(1), skyRadiance: texel(2) };
      } finally {
        probeScene.remove(mesh);
        material.dispose();
        target.dispose();
      }
    },

    probeSky(dirsLocal, sunLocal) {
      if (disposed) return { ok: false, why: 'the atmosphere is disposed' };
      const n = dirsLocal.length;
      if (n < 1 || n > PROBE_SKY_N) return { ok: false, why: `probeSky takes 1 to ${PROBE_SKY_N} directions, not ${n}` };
      const dirs = Array.from({ length: PROBE_SKY_N }, (_, i) => new THREE.Vector3(...(dirsLocal[i] ?? [0, 1, 0])));
      const material = new THREE.ShaderMaterial({
        uniforms: { ...uniforms, uProbeDirs: { value: dirs }, uProbeSun: { value: new THREE.Vector3(...sunLocal) } },
        vertexShader: FULLSCREEN_VERT,
        fragmentShader: glslHead + PROBE_SKY_FRAG,
        depthTest: false,
        depthWrite: false,
      });
      material.toneMapped = false;
      const target = makeTarget(n, 1, `${row.id}-sky-probe`);
      const mesh = new THREE.Mesh(quadGeometry, material);
      const probeScene = new THREE.Scene();
      probeScene.add(mesh);
      try {
        const prevTarget = renderer.getRenderTarget();
        renderer.setRenderTarget(target);
        renderer.render(probeScene, lutCamera);
        const buf = new Uint16Array(n * 4);
        renderer.readRenderTargetPixels(target, 0, 0, n, 1, buf);
        renderer.setRenderTarget(prevTarget);
        const radiance = Array.from({ length: n }, (_, i): readonly [number, number, number] => [
          THREE.DataUtils.fromHalfFloat(buf[i * 4]!),
          THREE.DataUtils.fromHalfFloat(buf[i * 4 + 1]!),
          THREE.DataUtils.fromHalfFloat(buf[i * 4 + 2]!),
        ]);
        return { ok: true, radiance };
      } finally {
        probeScene.remove(mesh);
        material.dispose();
        target.dispose();
      }
    },

    dispose(): void {
      if (disposed) return;
      disposed = true;
      lutScene.remove(quad);
      quadGeometry.dispose();
      for (const name of LUT_NAMES) {
        materials[name].dispose();
        targets[name].dispose();
      }
      skyViewPrev.dispose();
      dust?.dispose();
      if (groundDiffuse) {
        groundDiffuse.blank.dispose();
        groundDiffuse.target.dispose();
        groundDiffuse.material.dispose();
      }
    },
  };
  return handle;
}
